import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import countryList from 'react-select-country-list';
import {
  UserPlus,
  Users,
  Mail,
  Phone,
  MapPin,
  RefreshCw,
  Save,
  Camera,
  Calendar,
  X,
  Upload,
  Search,
  ChevronDown,
  Check,
  FileText,
  Plus,
  UserRound,
  Image as ImageIcon,
  Percent,
  AlertTriangle,
  CalendarOff,
  Calculator,
  Award
} from "lucide-react";
import { Toast } from "../components/ui/Toast";
import { LongToast, shouldUseLongToast } from '../components/ui/LongToast';
import { api } from '../services/api';
import { imprimerRecu, lireReglagesImpression } from '../utils/imprimerRecu';
import { useFeatures } from '../contexts/FeaturesContext';
import ConfirmationModal from './ConfirmationModal';
import CarteChoix from '../components/ui/CarteChoix';
import CalculProrata from '../components/ui/CalculProrata';
import ProtectedComponent from '../components/ProtectedComponent';
import { useHabilitationCaisse } from '../hooks/useHabilitationCaisse';
import BandeauCaisseReservee from '../components/caisse/BandeauCaisseReservee';
import GroupedClassDropdown from '../components/ui/GroupedClassDropdown';
import FamilleNombreuseStep from './FamilleNombreuseStep';
import ClassCapacityInfo from '../components/classes/ClassCapacityInfo';
import ChoixTransport, { SANS_BUS, transportIncomplet } from '../components/transport/ChoixTransport';
import { transport as apiTransport } from '../components/transport/apiTransport';

const REGISTRATION_TYPE = 'new';

const DEFAULT_PARENT = {
  relation: 'tuteur',
  nom_prenom: '',
  email: '',
  telephones: [''],
  adresse: '',
  principal: true
};

const parentRelationOptions = [
  { value: 'pere', label: 'Père' },
  { value: 'mere', label: 'Mère' },
  { value: 'tuteur', label: 'Tuteur' },
  { value: 'autre', label: 'Autre responsable' }
];

const sexOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' }
];

const formatFileSize = (size = 0) => {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

const SearchableDropdown = ({
  value,
  options,
  onChange,
  placeholder = 'Sélectionner...',
  disabled = false,
  searchable = true,
  emptyLabel = 'Aucune option disponible'
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  const selectedOption = options.find(option => String(option.value) === String(value));
  const filteredOptions = options.filter(option =>
    String(option.label || '').toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative mt-1" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen(prev => !prev);
        }}
        className="w-full min-h-[44px] px-4 py-2.5 border border-gray-200 rounded-lg bg-white text-left flex items-center justify-between gap-3 focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown size={18} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">{emptyLabel}</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 hover:bg-[#eef7fb] ${String(option.value) === String(value) ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'
                    }`}
                >
                  <span className="text-sm font-medium">{option.label}</span>
                  {String(option.value) === String(value) && <Check size={16} />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NewRegistration = () => {
  // Sans comptabilité, les étapes 4 et 5 cessent d'être des points de saisie
  // financière : elles annoncent le tarif et récapitulent, sans encaisser.
  // L'échéancier, lui, est créé dans tous les cas.
  const { estActive } = useFeatures();
  const comptabiliteOuverte = estActive('module.comptabilite');
  // Encaisser est réservé aux profils comptables (hooks/useHabilitationCaisse.js) :
  // sans habilitation, l'étape des frais passe en lecture et l'élève est
  // inscrit sans encaissement.
  const { habilite: habiliteCaisse } = useHabilitationCaisse();

  const [identityDoc, setIdentityDoc] = useState(null);
  const [diplomas, setDiplomas] = useState([]);
  const [otherDocs, setOtherDocs] = useState([]);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const confirmSubmissionRef = useRef(false);

  // Le reçu de caisse de l'inscription (RecuCaisseService, émis par le serveur
  // avec elle) : imprimé seul si l'école a réglé l'impression automatique
  // (Paramétrage → Reçus et impression), sinon il attend dans le Dossier
  // financier de l'élève (décision de Daniel du 04/11/2026).
  const reglagesImpression = useRef(null);
  useEffect(() => {
    lireReglagesImpression().then((r) => { reglagesImpression.current = r; }).catch(() => {});
  }, []);
  const [error, setError] = useState(null);
  const [toastInfo, setToastInfo] = useState(null);
  const countries = useMemo(() => countryList().getData(), []);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [activeYear, setActiveYear] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classeSelectionnee, setClasseSelectionnee] = useState(null);
  const [classCapacity, setClassCapacity] = useState(null);
  const [classCapacityLoading, setClassCapacityLoading] = useState(false);
  const [sections, setSections] = useState([]);

  const [familleInfo, setFamilleInfo] = useState({
    appartient_famille: false,
    famille_id: null,
    famille_nom: '',
    reduction_inscription: 0,          // 🆕 Montant réduction inscription
    reduction_mensuel: 0,              // 🆕 Montant réduction mensuelle
    reduction_exceptionnelle: false,
    reduction_motif: '',
    politique_ecole: false,
    politique_type: null, // 'admis_examen' | 'premier_classe' | 'premiers_inscrits'
    politique_motif: '',
    utilise_reduction_famille: true, // 🆕 Par défaut TRUE
    reduction_custom_motif: '' // 🆕 Motif si règles custom
  });


  // ⚠️ LES PLAFONDS DU SERVEUR, ÉCRITS ICI POUR QUE L'UTILISATEUR LES APPRENNE
  // AVANT D'ENVOYER. `documentUpload.array('documents', 5)` pour les diplômes et
  // `array('documents', 10)` pour les autres pièces (backend2/routes/eleves.routes.js).
  // Au-delà, multer rend LIMIT_UNEXPECTED_FILE et AUCUNE pièce du lot n'est
  // enregistrée — pas même les premières. Cet écran laissait empiler sans limite,
  // et la secrétaire repartait en croyant les diplômes joints.
  //
  // ⚠️ Les deux limites sont désormais écrites des deux côtés : les faire bouger
  // ensemble.
  const PLAFOND_PIECES = { diplomas: 5, other: 10 };

  // Le serveur n'accepte que PDF et images (`fileFilter`). L'attribut `accept` de
  // l'input n'est qu'une suggestion du navigateur, pas un garde-fou : on peut
  // toujours choisir « Tous les fichiers » dans le sélecteur.
  const formatAccepte = (file) => file.type === 'application/pdf' || String(file.type).startsWith('image/');

  // ⚠️ LES REFUS PASSENT PAR LE TOAST, PAS PAR `setError`.
  // L'état `error` de cet écran n'est LU NULLE PART : 21 écritures, zéro lecture.
  // Un refus posé par `setError` ne s'afficherait pas — l'utilisateur choisirait
  // 6 diplômes, rien n'apparaîtrait, et le bouton « Charger » aurait l'air cassé,
  // ce qui serait pire que le défaut corrigé. `long: true` est obligatoire : le
  // toast court ignore le type `warning` et se referme au bout de 3 secondes.
  const refuserFichiers = (message) => {
    setToastInfo({ title: 'Fichiers non ajoutés', message, type: 'warning', long: true });
  };

  const handleMultipleFileChange = (e, type) => {
    const files = Array.from(e.target.files || []);
    // Vidé tout de suite : sans cela, rechoisir le même fichier ne déclenche rien.
    e.target.value = '';
    if (files.length === 0) return;

    const tailleMax = 10 * 1024 * 1024;
    const tropGros = files.filter((file) => file.size > tailleMax);
    if (tropGros.length > 0) {
      refuserFichiers(`Trop volumineux (10 Mo maximum) : ${tropGros.map((file) => file.name).join(', ')}.`);
      return;
    }

    const refuses = files.filter((file) => !formatAccepte(file));
    if (refuses.length > 0) {
      refuserFichiers(
        `Format non accepté : ${refuses.map((file) => file.name).join(', ')}.`
        + ' Seuls les fichiers PDF et les images sont acceptés.'
      );
      return;
    }

    const plafond = PLAFOND_PIECES[type];
    const dejaChoisis = type === 'diplomas' ? diplomas.length : otherDocs.length;
    if (plafond && dejaChoisis + files.length > plafond) {
      refuserFichiers(
        `${plafond} fichiers au maximum, et ${dejaChoisis} déjà choisi${dejaChoisis > 1 ? 's' : ''}.`
        + ` Retirez-en avant d'en ajouter : au-delà de cette limite, le serveur rejette le lot entier.`
      );
      return;
    }

    switch (type) {
      case 'diplomas':
        setDiplomas((prev) => [...prev, ...files]);
        break;
      case 'other':
        setOtherDocs((prev) => [...prev, ...files]);
        break;
      default:
        break;
    }
  };

  const [studentInfo, setStudentInfo] = useState({
    nom: '',
    prenom: '',
    sexe: '',
    dateNaissance: '',
    lieuNaissance: '',
    nationalite: '',
    classe_id: ''
  });

  const [parentInfo, setParentInfo] = useState({
    nom_prenom: '',
    email: '',
    telephone: '',
    adresse: ''
  });
  const [parentMode, setParentMode] = useState('single');
  const [parentsInfo, setParentsInfo] = useState([{ ...DEFAULT_PARENT }]);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);



  const [finance, setFinance] = useState({
    inscription: { montant: 0, paye: false, statut: 'Non payé' },
    paiements_mensuels: {} // Structure dynamique basÃÂ©e sur les mois scolaires
  });

  // Le bus (lot 3a, 03/11/2026) : sans bus, au mois ou au ticket. Le bloc ne
  // s'affiche que si le registre du transport est installé dans l'école.
  const [transportChoix, setTransportChoix] = useState(SANS_BUS);
  const [reglagesTransport, setReglagesTransport] = useState(null);
  useEffect(() => {
    apiTransport.reglages().then(setReglagesTransport).catch(() => setReglagesTransport(null));
  }, []);

  // === MODIFICATION 3: Ajouter une fonction pour charger les mois scolaires ===
  // Ajoutez cette fonction avant les useEffect existants :

  const loadMoisScolaires = async () => {
    try {
      setIsLoadingMois(true);
      const response = await api.moisScolaires.getAll();

      if (response.success && Array.isArray(response.data)) {
        // Trier les mois par trimestre et par ordre
        const moisTries = response.data.sort((a, b) => {
          if (a.trimestre !== b.trimestre) {
            return a.trimestre - b.trimestre;
          }
          return a.ordre - b.ordre;
        });

        setMoisScolaires(moisTries);

        // Initialiser les paiements mensuels pour tous les mois
        const paiementsMensuels = {};
        moisTries.forEach(mois => {
          paiementsMensuels[mois.id] = {
            mois_id: mois.id,
            mois_nom: mois.nom,
            trimestre: mois.trimestre,
            montant_attendu: 0, // Sera mis ÃÂ  jour lors de la sÃÂ©lection de classe
            montant_paye: 0,
            paye: false,
            statut: 'Non payé'
          };
        });

        setFinance(prev => ({
          ...prev,
          paiements_mensuels: paiementsMensuels
        }));

        console.log("Mois scolaires chargés:", moisTries);
      } else {
        console.error("Format de donnÃÂ©es inattendu pour les mois scolaires");
        setError("Erreur lors du chargement des mois scolaires");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des mois scolaires:", error);
      setError("Erreur lors du chargement des mois scolaires");
    } finally {
      setIsLoadingMois(false);
    }
  };


  const [moisScolaires, setMoisScolaires] = useState([]);
  // L'eleve n'a pas forcement frequente l'ecole des le premier mois. Sans cette
  // date, le parcours creait neuf mois dus pour un eleve arrive en janvier, et la
  // famille voyait ses mois deja regles basculer en « partiel » sans explication.
  const [dateEntree, setDateEntree] = useState('');
  const [traitementMoisAnterieurs, setTraitementMoisAnterieurs] = useState('non_scolarise');
  const [traitementMoisEntree, setTraitementMoisEntree] = useState('prorata');
  const [ajustementsMois, setAjustementsMois] = useState([]);
  // Le montant d'inscription affiche au dernier passage a l'etape 4 : sa case
  // « Payé » y a ete cochee pour ce montant-la. L'effet de recalcul s'en sert
  // pour la decocher si le montant a change depuis. Les mois n'en ont pas
  // besoin : chaque ligne porte la somme pour laquelle elle a ete cochee.
  const montantInscriptionEtape4Ref = useRef(null);

  // Les politiques d'ecole, dites en clair. Les emojis d'origine (diplome, coupe,
  // eclair) ne sont pas des icones de l'application : la charte n'utilise que
  // Lucide.
  const LIBELLES_POLITIQUE = {
    admis_examen: 'Admis aux examens',
    premier_classe: 'Premier de classe',
    premiers_inscrits: 'Parmi les 100 premiers inscrits'
  };

  // FCFA sans decimales, separateur de milliers : la convention de l'application.
  const fcfa = (montant) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant) || 0))} FCFA`;


  // Le jour d'un horodatage du calendrier, ramene a « AAAA-MM-JJ ».
  //
  // `mois_scolaires.date_debut` vaut minuit dans le fuseau de l'ecole ; il arrive
  // ici en UTC (« 2025-09-30T22:00:00Z » pour le 1er octobre). Couper la chaine
  // donnerait le 30 septembre, et l'ecran proposerait un prorata a un eleve entre
  // des le premier jour. On arrondit donc au jour le plus proche - ce qui reste
  // juste quel que soit le fuseau du navigateur, tant que l'ecart reste sous 12 h.
  const jourCalendrier = (valeur) => {
    if (!valeur) return null;
    const texte = String(valeur);
    if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return texte;
    const instant = new Date(texte);
    if (Number.isNaN(instant.getTime())) return null;
    return new Date(instant.getTime() + 12 * 3600 * 1000).toISOString().slice(0, 10);
  };
  const [moisReglesFamille, setMoisReglesFamille] = useState([]);
  const [isLoadingMois, setIsLoadingMois] = useState(false);

  const countryOptions = useMemo(
    () => countries.map(country => ({ value: country.label, label: country.label })),
    [countries]
  );

  useEffect(() => {
    const mainParent = parentsInfo[0] || DEFAULT_PARENT;
    const telephones = (mainParent.telephones || []).filter(Boolean);
    setParentInfo({
      nom_prenom: mainParent.nom_prenom || '',
      email: mainParent.email || '',
      telephone: telephones[0] || '',
      adresse: mainParent.adresse || ''
    });
  }, [parentsInfo]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);



  // 🆕 Charger les détails de la classe sélectionnée
  const chargerDetailsClasse = async (classeId) => {
    try {
      const response = await api.classes.getById(classeId);
      if (response && response.success) {
        setClasseSelectionnee(response.data);
        console.log('Détails classe chargés:', response.data);
      } else if (response) {
        // Format direct sans success
        setClasseSelectionnee(response);
        console.log('Détails classe chargés (format direct):', response);
      }
    } catch (error) {
      console.error('Erreur chargement détails classe:', error);
      setClasseSelectionnee(null);
    }
  };

  const chargerCapaciteClasse = async (classeId) => {
    if (!classeId) {
      setClassCapacity(null);
      return;
    }

    setClassCapacityLoading(true);
    try {
      const response = await api.classes.getCapacity(classeId, activeYear?.id || studentInfo.annee_scolaire_id);
      if (response?.success) {
        setClassCapacity(response.data);
      } else {
        setClassCapacity(null);
        console.warn('Capacite classe indisponible:', response?.message || response?.error);
      }
    } catch (error) {
      console.error('Erreur chargement capacite classe:', error);
      setClassCapacity(null);
    } finally {
      setClassCapacityLoading(false);
    }
  };



  // Le matricule n'est plus tire ici. L'ecran en tirait un au hasard, que le
  // serveur ignorait pour en tirer un autre : celui qu'on aurait pu noter
  // n'etait pas celui enregistre. Il est attribue par le serveur, selon le
  // modele de l'ecole, et annonce dans le message de succes.



  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const classesData = await api.classes.getAll();
        if (Array.isArray(classesData)) {
          setClasses(classesData);
        } else {
          console.error('Les données des classes ne sont pas un tableau:', classesData);
          setClasses([]);
        }

        // ✅ NOUVEAU : Charger les sections
        const sectionsResponse = await api.sections.getAll();
        if (sectionsResponse?.success && Array.isArray(sectionsResponse.data)) {
          setSections(sectionsResponse.data);
          console.log('Sections chargées:', sectionsResponse.data);
        } else {
          console.error('Les données des sections ne sont pas un tableau:', sectionsResponse);
          setSections([]);
        }

        const yearResponse = await api.anneesScolaires.getActive();
        if (yearResponse.success && yearResponse.data) {
          setActiveYear(yearResponse.data);
        } else {
          setError("Aucune année scolaire active n'est définie");
        }

        // NOUVEAU : Charger les mois scolaires
        await loadMoisScolaires();

        await checkEditMode();
      } catch (error) {
        console.error('Erreur dans loadInitialData:', error);
        setError("Erreur lors du chargement des données initiales");
        setClasses([]);
      }
    };

    loadInitialData();
  }, []);



  // Recalculer les frais si on est au step 4 et qu'une classe est sÃ©lectionnÃ©e
  useEffect(() => {
    // VÃ©rifier si on est au Step 4 avec une classe sÃ©lectionnÃ©e
    if (currentStep === 4 && studentInfo.classe_id) {
      // Recalcul a chaque passage a l etape 4, avec ou sans reduction.
      //
      // Avant, seul le cas « avec reduction » etait recalcule : revenir a
      // « Aucune » apres l etape 4 laissait l ecran sur l ancienne reduction
      // (« Offerte - rien a encaisser », mois reduits) alors que l envoi part
      // sans reduction et que le serveur enregistre le plein tarif. Sans
      // reduction, le calcul ci-dessous rend simplement le tarif de la classe.
      const selectedClass = classes.find(c => c.id === studentInfo.classe_id);
      if (selectedClass) {
        console.log("?? Recalcul des frais avec rÃ©duction au Step 4");


        // 🆕 Calculer la réduction (famille OU exceptionnelle) - AVEC MONTANTS


        const fraisInscriptionNormal = Number(selectedClass.frais_inscription) || 0;
        const fraisMensuelNormal = Number(selectedClass.frais_mensuel) || 0;

        let reductionInscription = 0;
        let reductionMensuel = 0;
        let typeReduction = "Aucune";

        // 🆕 PRIORITÉ 1 : Politique d'école
        if (familleInfo.politique_ecole && familleInfo.politique_type) {
          reductionInscription = fraisInscriptionNormal;
          reductionMensuel = familleInfo.reduction_mensuel || 0;

          const labels = {
            'admis_examen': 'Admis aux examens',
            'premier_classe': 'Premier de classe',
            'premiers_inscrits': '100 premiers inscrits'
          };
          typeReduction = `Politique d'école (${labels[familleInfo.politique_type]})`;
        }
        // PRIORITÉ 2 : Famille nombreuse
        else if (familleInfo.appartient_famille && familleInfo.famille_id) {
          reductionInscription = familleInfo.reduction_inscription || 0;
          reductionMensuel = familleInfo.reduction_mensuel || 0;
          typeReduction = `Famille nombreuse (${familleInfo.famille_nom})`;
        }
        // PRIORITÉ 3 : Réduction exceptionnelle
        else if (familleInfo.reduction_exceptionnelle) {
          reductionInscription = familleInfo.reduction_inscription || 0;
          reductionMensuel = familleInfo.reduction_mensuel || 0;
          typeReduction = "Réduction exceptionnelle";
        }


        // Appliquer la réduction (soustraction directe)
        const fraisInscriptionFinal = Math.max(0, fraisInscriptionNormal - reductionInscription);
        const fraisMensuelFinal = Math.max(0, fraisMensuelNormal - reductionMensuel);

        // La case « Payé » de l'inscription, cochee a l'etape 4, l'a ete pour le
        // montant affiche a ce moment-la. Si ce montant a change depuis
        // (reduction modifiee a l'etape 2, autre classe a l'etape 1), la laisser
        // cochee enverrait un encaissement qui n'est plus le bon : le serveur
        // enregistrerait l'inscription reglee au nouveau montant, sans que la
        // caisse l'ait vu. On decoche la ligne et on le dit, par l'avertissement
        // des ajustements (`ajustementsMois`).
        //
        // La reference est le montant du precedent passage a l'etape 4, pas
        // l'etat courant : un changement de classe a deja reecrit le montant
        // avant qu'on revienne ici. Au premier passage il n'y a rien a comparer,
        // et une inscription dont le montant ne change pas n'est pas touchee.
        //
        // Les mois ne passent pas par ici : leur du depend aussi de la date
        // d'entree et du prorata, qui changent a l'etape 4 meme. Ils sont tenus
        // ligne a ligne par l'effet de la date d'entree, plus bas.
        const inscriptionPrecedente = montantInscriptionEtape4Ref.current;
        montantInscriptionEtape4Ref.current = fraisInscriptionFinal;
        const decocherInscription = inscriptionPrecedente !== null
          && inscriptionPrecedente !== fraisInscriptionFinal
          && Boolean(finance.inscription?.paye);


        console.log("?? Frais recalculÃ©s:", {
          inscription: { normal: fraisInscriptionNormal, final: fraisInscriptionFinal },
          mensuel: { normal: fraisMensuelNormal, final: fraisMensuelFinal }
        });

        // Mettre Ã  jour finance
        setFinance(prev => {
          const newFinance = { ...prev };

          newFinance.inscription = {
            ...newFinance.inscription,
            montant_normal: fraisInscriptionNormal,
            montant_reduction: fraisInscriptionNormal - fraisInscriptionFinal,
            montant_final: fraisInscriptionFinal,
            montant: fraisInscriptionFinal,
            ...(decocherInscription ? { paye: false, statut: 'Non payé' } : {}),
            type_reduction: typeReduction // ?? TraÃ§abilitÃ©
          };

          const updatedPaiementsMensuels = { ...newFinance.paiements_mensuels };
          Object.keys(updatedPaiementsMensuels).forEach(moisId => {
            updatedPaiementsMensuels[moisId] = {
              ...updatedPaiementsMensuels[moisId],
              montant_normal: fraisMensuelNormal,
              montant_reduction: fraisMensuelNormal - fraisMensuelFinal,
              montant_attendu: fraisMensuelFinal,
              montant_final: fraisMensuelFinal,
              type_reduction: typeReduction // ?? TraÃ§abilitÃ©
            };
          });

          newFinance.paiements_mensuels = updatedPaiementsMensuels;

          console.log("? Finance recalculÃ©:", newFinance);
          return newFinance;
        });

        // L'avertissement nomme l'inscription et le montant pour lequel elle avait
        // ete cochee : c'est ce que la caisse a encaisse. Une inscription deja
        // signalee, cochee puis decochee a nouveau, prend sa nouvelle valeur.
        if (decocherInscription) {
          setAjustementsMois((prev) => [
            ...prev.filter((item) => item.id !== 'inscription'),
            { id: 'inscription', nom: 'Inscription', type: 'decoche', montant: inscriptionPrecedente }
          ]);
        }
      }
    }
    // `finance` est lu sans etre une dependance : l'effet ecrit `finance`, il se
    // relancerait lui-meme a chaque tour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    studentInfo.classe_id,
    familleInfo.reduction_inscription,      // ✅
    familleInfo.reduction_mensuel,          // ✅
    familleInfo.appartient_famille,
    familleInfo.famille_id,
    familleInfo.reduction_exceptionnelle,
    familleInfo.politique_ecole,
    familleInfo.politique_type,
    classes
  ]);


  const handleFileChange = (e, type) => {
    const files = e.target.files;
    if (!files) return;

    if (type === 'identity') {
      const file = files[0];
      if (file.size > 5 * 1024 * 1024) {
        setError("La taille du fichier ne doit pas dÃÂ©passer 5MB");
        return;
      }
      setIdentityDoc(file);
      e.target.value = '';
    } else if (type === 'diplomas') {
      Array.from(files).forEach(file => {
        if (file.size > 10 * 1024 * 1024) {
          setError("La taille de chaque fichier ne doit pas dÃÂ©passer 10MB");
          return;
        }
      });
      setDiplomas(prev => [...prev, ...Array.from(files)]);
      e.target.value = '';
    } else if (type === 'other') {
      Array.from(files).forEach(file => {
        if (file.size > 10 * 1024 * 1024) {
          setError("La taille de chaque fichier ne doit pas dÃÂ©passer 10MB");
          return;
        }
      });
      setOtherDocs(prev => [...prev, ...Array.from(files)]);
      e.target.value = '';
    }
  };

  const checkExistingStudent = async (nom, prenom) => {
    try {
      const response = await api.eleves.checkExisting({
        nom,
        prenom,
        previous_year: activeYear.id - 1
      });
      if (response.found) {
        setPreviousData(response.data);
        if (window.confirm("ÃÂlÃÂ¨ve trouvÃÂ© dans l'annÃÂ©e prÃÂ©cÃÂ©dente. Voulez-vous copier ses informations ?")) {
          setStudentInfo({
            ...studentInfo,
            ...response.data,
            annee_scolaire_id: activeYear.id
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la vÃÂ©rification:", error);
    }
  };

  const checkEditMode = async () => {
    const editingId = localStorage.getItem('editingEleveId');
    if (editingId) {
      setLoading(true);
      try {
        const eleveData = await api.eleves.getById(editingId);
        if (eleveData) {
          setIsEditing(true);
          setStudentInfo({
            nom: eleveData.nom,
            prenom: eleveData.prenom,
            sexe: eleveData.sexe,
            dateNaissance: eleveData.dateNaissance,
            lieuNaissance: eleveData.lieuNaissance,
            nationalite: eleveData.nationalite,
            classe_id: eleveData.classe_id
          });
          setParentInfo(eleveData.parent);
          if (eleveData.parent) {
            setParentsInfo([{
              ...DEFAULT_PARENT,
              nom_prenom: eleveData.parent.nom_prenom || '',
              email: eleveData.parent.email || '',
              telephones: [eleveData.parent.telephone || ''],
              adresse: eleveData.parent.adresse || ''
            }]);
          }
          setFinance(eleveData.finance);
        }
      } catch (error) {
        setError("Erreur lors du chargement des donnÃÂ©es de l'ÃÂ©lÃÂ¨ve");
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("La taille de l'image ne doit pas dÃÂ©passer 5MB");
        return;
      }

      if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
        setError("Seuls les formats JPG et PNG sont acceptÃÂ©s");
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("La caméra n'est pas disponible dans ce navigateur.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 720 },
          height: { ideal: 720 }
        },
        audio: false
      });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 0);
    } catch (error) {
      console.error('Erreur ouverture caméra:', error);
      setCameraError("Impossible d'ouvrir la caméra. Vérifiez les autorisations du navigateur.");
    }
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    const sourceWidth = video.videoWidth || 640;
    const sourceHeight = video.videoHeight || 480;
    const cropSize = Math.min(sourceWidth, sourceHeight);
    const sourceX = Math.max(0, (sourceWidth - cropSize) / 2);
    const sourceY = Math.max(0, (sourceHeight - cropSize) / 2);

    canvas.width = 720;
    canvas.height = 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, sourceX, sourceY, cropSize, cropSize, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const clearPhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
    stopCamera();
  };

  const handleParentModeChange = (mode) => {
    setParentMode(mode);
    setParentsInfo(prev => {
      if (mode === 'single') {
        return [{ ...(prev[0] || DEFAULT_PARENT), principal: true }];
      }
      const firstParent = { ...(prev[0] || DEFAULT_PARENT), relation: prev[0]?.relation || 'pere', principal: true };
      const secondParent = prev[1]
        ? { ...prev[1], principal: false }
        : { ...DEFAULT_PARENT, relation: 'mere', principal: false };
      return [firstParent, secondParent];
    });
  };

  const handleParentChange = (index, field, value) => {
    setParentsInfo(prev => prev.map((parent, parentIndex) =>
      parentIndex === index ? { ...parent, [field]: value } : parent
    ));
  };

  const handleParentPhoneChange = (parentIndex, phoneIndex, value) => {
    setParentsInfo(prev => prev.map((parent, currentIndex) => {
      if (currentIndex !== parentIndex) return parent;
      const telephones = [...(parent.telephones || [''])];
      telephones[phoneIndex] = value;
      return { ...parent, telephones };
    }));
  };

  const addParentPhone = (parentIndex) => {
    setParentsInfo(prev => prev.map((parent, currentIndex) => {
      if (currentIndex !== parentIndex) return parent;
      if ((parent.telephones || []).length >= 2) return parent;
      return { ...parent, telephones: [...(parent.telephones || ['']), ''] };
    }));
  };

  const removeParentPhone = (parentIndex, phoneIndex) => {
    setParentsInfo(prev => prev.map((parent, currentIndex) => {
      if (currentIndex !== parentIndex) return parent;
      const telephones = (parent.telephones || ['']).filter((_, currentPhoneIndex) => currentPhoneIndex !== phoneIndex);
      return { ...parent, telephones: telephones.length > 0 ? telephones : [''] };
    }));
  };

  const buildParentsPayload = () => {
    return parentsInfo
      .map((parent, index) => {
        const telephones = (parent.telephones || [])
          .map(phone => String(phone || '').trim())
          .filter(Boolean);

        return {
          relation: parent.relation || (index === 0 ? 'tuteur' : 'autre'),
          nom_prenom: String(parent.nom_prenom || '').trim(),
          email: String(parent.email || '').trim(),
          telephone: telephones[0] || '',
          telephone_secondaire: telephones[1] || null,
          telephones,
          adresse: String(parent.adresse || '').trim(),
          principal: index === 0
        };
      })
      .filter(parent => parent.nom_prenom || parent.telephone || parent.email || parent.adresse);
  };


  const handleClasseChange = async (valueOrEvent) => {
    if (isEditing) return;

    const selectedClassId = parseInt(valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent, 10);
    setClassCapacity(null);

    // Charger les details et la capacite de la classe
    if (selectedClassId) {
      await Promise.all([
        chargerDetailsClasse(selectedClassId),
        chargerCapaciteClasse(selectedClassId)
      ]);
    }

    if (!selectedClassId) {
      console.log("Aucune classe sélectionnée");
      return;
    }

    try {
      const classeDetails = classes.find(classe => classe.id === selectedClassId);

      if (!classeDetails) {
        console.error("Classe non trouvée:", selectedClassId);
        setError("Classe non trouvée");
        return;
      }

      console.log("📚 Classe sélectionnée:", classeDetails);
      console.log("👨‍👩‍👧 Info famille:", familleInfo);

      setStudentInfo(prev => ({ ...prev, classe_id: selectedClassId }));




      // 🆕 Calculer la réduction (politique d'école OU famille OU exceptionnelle)
      const fraisInscriptionNormal = Number(classeDetails.frais_inscription) || 0;
      const fraisMensuelNormal = Number(classeDetails.frais_mensuel) || 0;

      let reductionInscription = 0;
      let reductionMensuel = 0;
      let typeReduction = "Aucune";

      // 🆕 PRIORITÉ 1 : Politique d'école (inscription GRATUITE)
      if (familleInfo.politique_ecole && familleInfo.politique_type) {
        reductionInscription = fraisInscriptionNormal; // Inscription = 0 FCFA
        reductionMensuel = familleInfo.reduction_mensuel || 0;

        const labels = {
          'admis_examen': 'Admis aux examens',
          'premier_classe': 'Premier de classe',
          'premiers_inscrits': '100 premiers inscrits'
        };
        typeReduction = `Politique d'école (${labels[familleInfo.politique_type]})`;
      }
      // PRIORITÉ 2 : Famille nombreuse
      else if (familleInfo.appartient_famille && familleInfo.famille_id) {
        reductionInscription = familleInfo.reduction_inscription || 0;
        reductionMensuel = familleInfo.reduction_mensuel || 0;
        typeReduction = `Famille nombreuse (${familleInfo.famille_nom})`;
      }
      // PRIORITÉ 3 : Réduction exceptionnelle
      else if (familleInfo.reduction_exceptionnelle) {
        reductionInscription = familleInfo.reduction_inscription || 0;
        reductionMensuel = familleInfo.reduction_mensuel || 0;
        typeReduction = "Réduction exceptionnelle";
      }



      console.log("💰 Frais normaux:", {
        inscription: fraisInscriptionNormal,
        mensuel: fraisMensuelNormal,
        reduction_inscription: reductionInscription,
        reduction_mensuel: reductionMensuel,
        type: typeReduction
      });

      // Appliquer la réduction (soustraction directe)
      const fraisInscriptionFinal = Math.max(0, fraisInscriptionNormal - reductionInscription);
      const fraisMensuelFinal = Math.max(0, fraisMensuelNormal - reductionMensuel);

      // Calculer le pourcentage pour l'affichage
      const pourcentageInscription = fraisInscriptionNormal > 0
        ? ((reductionInscription / fraisInscriptionNormal) * 100).toFixed(2)
        : 0;
      const pourcentageMensuel = fraisMensuelNormal > 0
        ? ((reductionMensuel / fraisMensuelNormal) * 100).toFixed(2)
        : 0;

      console.log("💸 Frais après réduction:", {
        inscription: fraisInscriptionFinal,
        mensuel: fraisMensuelFinal,
        reduction_inscription: reductionInscription,
        reduction_mensuel: reductionMensuel,
        pourcentage_inscription: pourcentageInscription + "%",
        pourcentage_mensuel: pourcentageMensuel + "%"
      });

      // Mettre à jour les frais d'inscription
      const newFinance = { ...finance };

      newFinance.inscription = {
        montant_normal: fraisInscriptionNormal,
        montant_reduction: reductionInscription,
        montant_final: fraisInscriptionFinal,
        montant: fraisInscriptionFinal,
        paye: finance.inscription?.paye || false,
        statut: finance.inscription?.statut || 'Non payé',
        type_reduction: typeReduction
      };

      // Mettre à jour les montants attendus pour tous les mois
      const updatedPaiementsMensuels = { ...newFinance.paiements_mensuels };
      Object.keys(updatedPaiementsMensuels).forEach(moisId => {
        updatedPaiementsMensuels[moisId] = {
          ...updatedPaiementsMensuels[moisId],
          montant_normal: fraisMensuelNormal,
          montant_reduction: reductionMensuel,
          montant_attendu: fraisMensuelFinal,
          montant_final: fraisMensuelFinal,
          type_reduction: typeReduction
        };
      });

      newFinance.paiements_mensuels = updatedPaiementsMensuels;
      setFinance(newFinance);

      console.log("✅ Finance mis à jour:", newFinance);

      // Afficher un récapitulatif dans la console
      if (reductionInscription > 0 || reductionMensuel > 0) {
        const economieInscription = reductionInscription;
        const economieMensuelle = reductionMensuel;
        const nombreMois = Object.keys(updatedPaiementsMensuels).length;
        const economieAnnuelle = economieInscription + (economieMensuelle * nombreMois);

        console.log("🎯 RÉCAPITULATIF ÉCONOMIE:", {
          type: typeReduction,
          economie_inscription: economieInscription.toLocaleString() + " FCFA",
          economie_mensuelle: economieMensuelle.toLocaleString() + " FCFA/mois",
          economie_annuelle_estimee: economieAnnuelle.toLocaleString() + " FCFA"
        });
      }

    } catch (error) {
      console.error("❌ Erreur détaillée:", error);
      setError("Erreur lors du chargement des détails de la classe");
    }
  };



  const handleCheckboxChange = (e, type) => {
    if (type === 'inscription') {
      setFinance(prev => ({
        ...prev,
        inscription: {
          ...prev.inscription,
          paye: e.target.checked,
          statut: e.target.checked ? 'Payé' : 'Non paye'
        }
      }));
    }
  };

  // `montantDuEffectif` est le montant reellement du pour ce mois : le tarif
  // plein d'ordinaire, mais le prorata quand l'eleve arrive en cours de mois.
  // Sans lui, cocher « Payé » sur un mois proratise encaissait le tarif plein et
  // creait un trop-percu que personne ne voyait.
  const handleMoisPaiement = (moisId, field, value, montantDuEffectif = null) => {
    setFinance(prev => {
      const updatedPaiements = { ...prev.paiements_mensuels };
      const moisData = { ...updatedPaiements[moisId] };
      const montantDu = montantDuEffectif === null
        ? Number(moisData.montant_attendu) || 0
        : Number(montantDuEffectif) || 0;
      // La caisse reprend la ligne en main : la somme qu'elle avait saisie avant
      // un plafonnement automatique n'est plus celle que porte la ligne.
      delete moisData.montant_saisi_avant_plafond;

      if (field === 'paye') {
        moisData.paye = value;
        if (value) {
          // Si payé, le montant payé = montant réellement dû
          moisData.montant_paye = montantDu;
          moisData.statut = 'Payé';
        } else {
          // Si non payÃÂ©, remettre ÃÂ  0
          moisData.montant_paye = 0;
          moisData.statut = 'Non paye';
        }
      } else if (field === 'montant_paye') {
        const montantPaye = Math.min(montantDu, Math.max(0, parseFloat(value) || 0));
        moisData.montant_paye = montantPaye;

        // DÃÂ©terminer le statut automatiquement
        if (montantDu > 0 && montantPaye >= montantDu) {
          moisData.paye = true;
          moisData.statut = 'Payé';
        } else if (montantPaye > 0) {
          moisData.paye = false;
          moisData.statut = 'Partiel';
        } else {
          moisData.paye = false;
          moisData.statut = 'Non payé';
        }
      }

      updatedPaiements[moisId] = moisData;

      return {
        ...prev,
        paiements_mensuels: updatedPaiements
      };
    });
  };


  // Le mois scolaire qui contient la date d'entree. Le calendrier de l'ecole est
  // la source de verite : on ne suppose ni que l'annee commence en octobre, ni
  // qu'un mois de paie suit le mois gregorien.
  const moisEntree = React.useMemo(() => {
    if (!dateEntree || moisScolaires.length === 0) return null;

    // Comparaison de jours et non d'instants : un mois qui se ferme le 31 doit
    // accueillir une entree le 31, ce qu'une comparaison d'horodatages UTC ratait.
    const jourEntree = jourCalendrier(dateEntree);
    if (!jourEntree) return null;

    const dansLaFenetre = moisScolaires.find((mois) => {
      const debut = jourCalendrier(mois.date_debut);
      const fin = jourCalendrier(mois.date_fin);
      return debut && fin && debut <= jourEntree && jourEntree <= fin;
    });
    if (dansLaFenetre) return dansLaFenetre;

    // Entre deux fenetres, ou avant la premiere : le premier mois qui commence
    // apres cette date. Au-dela du dernier, aucun mois n'est concerne.
    const suivants = moisScolaires
      .filter((mois) => {
        const debut = jourCalendrier(mois.date_debut);
        return debut && debut >= jourEntree;
      })
      .sort((a, b) => Number(a.ordre) - Number(b.ordre));
    return suivants[0] || null;
  }, [dateEntree, moisScolaires]);

  // Les mois que la famille a deja regles. Sert uniquement a avertir : sans cette
  // lecture, l'ecran ne pourrait pas dire ce que le second choix bouleverse.
  React.useEffect(() => {
    const familleId = familleInfo.famille_id;
    if (!familleId || !activeYear?.id) { setMoisReglesFamille([]); return; }

    let annule = false;
    (async () => {
      try {
        const rapport = await api.familles.getPaiementsParMois(familleId, activeYear.id);
        if (annule) return;
        const regles = (rapport?.data?.rapport_par_mois || [])
          .filter((mois) => Number(mois.total_paye || 0) > 0)
          .map((mois) => Number(mois.mois_id));
        setMoisReglesFamille(regles);
      } catch (erreur) {
        if (!annule) setMoisReglesFamille([]);
      }
    })();

    return () => { annule = true; };
  }, [familleInfo.famille_id, activeYear?.id]);

  // L'entree tombe-t-elle en cours de mois ? Si oui, le mois d'entree n'est ni du
  // en entier ni gratuit : il est du au prorata des jours frequentes. Le montant
  // affiche ici n'est qu'une estimation - le serveur recalcule et fait foi.
  const prorataEntree = React.useMemo(() => {
    if (!moisEntree || !dateEntree) return null;
    const debut = jourCalendrier(moisEntree.date_debut);
    const fin = jourCalendrier(moisEntree.date_fin);
    const entree = jourCalendrier(dateEntree);
    if (!debut || !fin || !entree) return null;
    if (entree <= debut || entree > fin) return null;

    const enJours = (t) => Math.round(Date.parse(`${t}T00:00:00Z`) / 86400000);
    const joursDuMois = enJours(fin) - enJours(debut) + 1;
    const joursPresents = enJours(fin) - enJours(entree) + 1;
    if (joursDuMois <= 0 || joursPresents <= 0 || joursPresents >= joursDuMois) return null;

    const plein = Number(finance.paiements_mensuels?.[moisEntree.id]?.montant_attendu) || 0;
    return {
      mois: moisEntree,
      joursPresents,
      joursDuMois,
      montantPlein: plein,
      montantProrata: Math.round(plein * (joursPresents / joursDuMois))
    };
  }, [moisEntree, dateEntree, finance.paiements_mensuels]);

  const moisAnterieurs = React.useMemo(() => {
    if (!moisEntree) return [];
    return moisScolaires
      .filter((mois) => Number(mois.ordre) < Number(moisEntree.ordre))
      .sort((a, b) => Number(a.ordre) - Number(b.ordre));
  }, [moisEntree, moisScolaires]);

  // Parmi les mois anterieurs, ceux que la famille a deja regles : les seuls dont
  // le total bougerait si l'eleve devait les payer.
  const moisAnterieursRegles = React.useMemo(() => (
    moisAnterieurs.filter((mois) => moisReglesFamille.includes(Number(mois.id)))
  ), [moisAnterieurs, moisReglesFamille]);

  // L'etat de chaque mois, deduit de la date d'entree. C'est la seule source que
  // le tableau des mois doit consulter : sans elle, il proposait d'encaisser un
  // mois que le bloc du dessus venait de declarer non scolarise.
  const etatsParMois = React.useMemo(() => {
    const etats = new Map();

    if (traitementMoisAnterieurs === 'non_scolarise') {
      for (const mois of moisAnterieurs) {
        etats.set(String(mois.id), { type: 'non_scolarise', montantDu: 0 });
      }
    }

    if (prorataEntree && traitementMoisEntree === 'prorata') {
      etats.set(String(prorataEntree.mois.id), {
        type: 'prorata',
        montantDu: prorataEntree.montantProrata,
        montantPlein: prorataEntree.montantPlein,
        joursPresents: prorataEntree.joursPresents,
        joursDuMois: prorataEntree.joursDuMois
      });
    }

    return etats;
  }, [traitementMoisAnterieurs, moisAnterieurs, prorataEntree, traitementMoisEntree]);

  // Une somme deja saisie doit etre remise d'aplomb SOUS LES YEUX de la caisse
  // quand ce qui est du change - par la date d'entree, le choix prorata ou plein,
  // la classe ou la reduction.
  //
  // Trois cas. Le mois devient non scolarise : la somme n'a plus lieu d'etre.
  // Auparavant elle restait affichee et n'etait effacee qu'a l'envoi, par la
  // charge utile - on pouvait taper 4 000 FCFA sur Octobre, cocher « Payé », puis
  // declarer l'entree en juin, et l'encaissement n'etait enregistre nulle part,
  // sans le moindre message.
  //
  // La case « Payé » est cochee : une ligne cochee doit porter exactement son du.
  // Si le du a change depuis, dans un sens ou dans l'autre, la somme cochee n'est
  // plus la bonne. Plafonnee en restant cochee, elle s'enregistrait « Partiel »
  // des que le du remontait, et la famille aurait ete relancee pour une somme
  // deja encaissee. On decoche la ligne et on dit pour quelle somme elle l'etait.
  // Le controle porte sur la ligne elle-meme et non sur le tarif : un aller et
  // retour de classe ou de date qui ramene le tarif d'origine ne le trompe pas.
  //
  // La case n'est pas cochee et le mois passe au prorata : une somme superieure
  // au prorata devient un trop-percu, on la ramene au montant du. La somme saisie
  // avant ce plafonnement reste notee sur la ligne : si le mois doit ensuite etre
  // decoche, c'est elle que la caisse a encaissee.
  React.useEffect(() => {
    const paiements = finance.paiements_mensuels || {};
    const aCorriger = [];
    Object.keys(paiements).forEach((cle) => {
      const donnees = paiements[cle];
      if (!donnees) return;
      const etat = etatsParMois.get(String(cle)) || null;
      const saisi = Number(donnees.montant_paye) || 0;

      if (etat?.type === 'non_scolarise') {
        if (saisi > 0 || donnees.paye === true) {
          aCorriger.push({ cle, montantCible: 0, type: 'efface' });
        }
        return;
      }

      const du = etat ? etat.montantDu : (Number(donnees.montant_attendu) || 0);
      const saisieAvantPlafond = Number(donnees.montant_saisi_avant_plafond) || 0;
      if (donnees.paye === true) {
        if (saisi !== du) {
          aCorriger.push({
            cle,
            montantCible: 0,
            type: 'decoche',
            // La somme portee sur la ligne avant toute correction automatique.
            annonce: saisieAvantPlafond > 0 ? saisieAvantPlafond : saisi,
            saisie: saisieAvantPlafond > 0
          });
        }
      } else if (etat?.type === 'prorata' && saisi > du) {
        aCorriger.push({
          cle,
          montantCible: du,
          type: 'plafonne',
          avant: saisieAvantPlafond > 0 ? saisieAvantPlafond : saisi
        });
      }
    });

    if (aCorriger.length > 0) {
      setFinance((prev) => {
        const lignes = { ...prev.paiements_mensuels };
        for (const item of aCorriger) {
          const ligne = {
            ...lignes[item.cle],
            montant_paye: item.montantCible,
            paye: item.montantCible > 0,
            statut: item.montantCible > 0 ? 'Payé' : 'Non payé'
          };
          if (item.type === 'plafonne' && item.montantCible > 0) {
            ligne.montant_saisi_avant_plafond = item.avant;
          } else {
            delete ligne.montant_saisi_avant_plafond;
          }
          lignes[item.cle] = ligne;
        }
        return { ...prev, paiements_mensuels: lignes };
      });
    }

    // Une ligne n'a qu'un avertissement, le dernier : un mois decoche apres avoir
    // ete plafonne ne dit plus « ramene a », puisque son champ est vide.
    //
    // Quand la date d'entree ne change plus rien (pas de date, ou tous les mois
    // dus en entier), ses avertissements (remis a zero, ramene au prorata) n'ont
    // plus d'objet et disparaissent ; les cases decochees restent signalees,
    // elles ne doivent rien a la date. Rien a corriger : on ne touche
    // pas a ce qui est affiche. Le vider ici le ferait disparaitre au tour
    // suivant - celui declenche par la correction elle-meme - et la caisse
    // n'aurait jamais le temps de le lire. Meme reference quand rien ne change,
    // sinon l'effet se rappelle lui-meme indefiniment.
    const nomsParId = new Map(moisScolaires.map((mois) => [String(mois.id), mois.nom]));
    const nouveaux = aCorriger.map((item) => ({
      id: String(item.cle),
      nom: nomsParId.get(String(item.cle)) || paiements[item.cle]?.mois_nom || `Mois ${item.cle}`,
      type: item.type,
      montant: item.type === 'decoche' ? item.annonce : item.montantCible,
      ...(item.saisie ? { saisie: true } : {})
    }));
    const corriges = new Set(nouveaux.map((item) => item.id));
    const sansDate = etatsParMois.size === 0;
    setAjustementsMois((prev) => {
      const gardes = prev.filter((item) => (
        !corriges.has(item.id)
        && !(sansDate && (item.type === 'efface' || item.type === 'plafonne'))
      ));
      if (nouveaux.length === 0 && gardes.length === prev.length) return prev;
      return [...gardes, ...nouveaux];
    });
  }, [etatsParMois, finance.paiements_mensuels, moisScolaires]);


  const getMoisParTrimestre = () => {
    const trimestres = {};

    moisScolaires.forEach(mois => {
      const trimestre = mois.trimestre;
      if (!trimestres[trimestre]) {
        trimestres[trimestre] = [];
      }
      trimestres[trimestre].push(mois);
    });

    return trimestres;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentStep === 3) {
      const invalidPhone = parentsInfo.some(parent =>
        (parent.telephones || []).some(phone => String(phone || '').includes('/'))
      );

      if (invalidPhone) {
        setError("Chaque numéro de téléphone doit être saisi dans un champ séparé, sans slash.");
        setToastInfo({
          message: "Séparez les numéros de téléphone dans les champs prévus.",
          type: 'error'
        });
        return;
      }
    }

    if (currentStep === 5) {
      // 🆕 DEBUG COMPLET
      console.log("=== 1️⃣ DEBUG handleSubmit (AVANT MODAL) ===");
      console.log("1. moisScolaires chargés:", moisScolaires);
      console.log("2. finance.paiements_mensuels:", finance.paiements_mensuels);
      console.log("3. Nombre de mois:", Object.keys(finance.paiements_mensuels || {}).length);
      console.log("4. Exemple d'un mois:", finance.paiements_mensuels[Object.keys(finance.paiements_mensuels || {})[0]]);
      console.log("5. Type de paiements_mensuels:", typeof finance.paiements_mensuels);
      console.log("6. familleInfo:", familleInfo);
      console.log("==========================================");

      // Vérifications
      if (!activeYear) {
        setError("Impossible de procéder à l'inscription : aucune année scolaire active n'est définie");
        setToastInfo({
          message: "Erreur : aucune année scolaire active n'est définie",
          type: 'error'
        });
        return;
      }

      if (familleInfo.appartient_famille && !familleInfo.famille_id) {
        setError("Veuillez sélectionner ou créer une famille nombreuse");
        setToastInfo({
          message: "Veuillez compléter les informations de la famille nombreuse",
          type: 'error'
        });
        setCurrentStep(2);
        return;
      }

      // Une politique d'ecole sans categorie n'est pas appliquee a l'envoi
      // (handleConfirmSubmission exige politique_type), alors que l'etape 4
      // annonce deja « Inscription offerte » : le serveur facturerait le plein
      // tarif. On renvoie l'agent choisir la categorie.
      if (familleInfo.politique_ecole && !familleInfo.politique_type) {
        setError("Choisissez la catégorie de la politique d'école");
        setToastInfo({
          message: "Choisissez la catégorie de la politique d'école avant de continuer",
          type: 'error'
        });
        setCurrentStep(2);
        return;
      }

      if (!finance.paiements_mensuels) {
        setError("Erreur : les paiements mensuels ne sont pas initialisés");
        console.error("❌ finance.paiements_mensuels est undefined/null");
        return;
      }

      if (Object.keys(finance.paiements_mensuels).length === 0) {
        setError("Erreur : aucun mois scolaire chargé. Veuillez recharger la page.");
        console.error("❌ Aucun mois dans paiements_mensuels");
        return;
      }

      // Le bus par zones (lot 3a bis) : sans zone ni lieu, le serveur
      // refuserait l'inscription entière ; on le dit avant.
      const busIncomplet = reglagesTransport?.installe ? transportIncomplet(transportChoix, { reglages: reglagesTransport }) : null;
      if (busIncomplet) {
        setError(busIncomplet);
        setToastInfo({ message: busIncomplet, type: 'error' });
        return;
      }

      setShowConfirmation(true);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };



  const handleConfirmSubmission = async () => {
    if (confirmSubmissionRef.current) {
      return;
    }

    if (!activeYear) {
      setError("Aucune année scolaire active n'est définie");
      return;
    }

    confirmSubmissionRef.current = true;
    setLoading(true);
    setError(null);
    setToastInfo(null);


    try {
      const parentsPayload = buildParentsPayload();
      const primaryParent = parentsPayload[0] || {
        nom_prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        relation: 'tuteur',
        telephones: []
      };

      // La politique d'ecole part au serveur comme une reduction exceptionnelle,
      // comme le fait deja la reinscription (reinscription.routes.js, mode
      // « politique »).
      //
      // Le choix « politique » remet `reduction_exceptionnelle` a faux : jusqu'ici
      // l'ecran annoncait « Offerte - rien a encaisser » mais n'envoyait aucune
      // reduction. Le serveur, dont le calcul ignore `politique_ecole`, enregistrait
      // alors l'inscription au plein tarif, non payee, et chaque mois au plein
      // tarif. On envoie ce que l'ecran affiche : l'inscription entiere de la
      // classe choisie, et la reduction mensuelle saisie. Le motif est exige par
      // le serveur des qu'une reduction exceptionnelle est accordee.
      const politiqueAppliquee = Boolean(familleInfo.politique_ecole && familleInfo.politique_type);
      const classeChoisie = classes.find((classe) => String(classe.id) === String(studentInfo.classe_id));
      const reductionsExceptionnelles = politiqueAppliquee
        ? {
            inscription: Number(classeChoisie?.frais_inscription) || 0,
            mensuel: Number(familleInfo.reduction_mensuel) || 0,
            motif: (familleInfo.politique_motif || '').trim()
              || `Politique d'école - ${LIBELLES_POLITIQUE[familleInfo.politique_type] || familleInfo.politique_type}`
          }
        : familleInfo.reduction_exceptionnelle
          ? {
              inscription: familleInfo.reduction_inscription || 0,
              mensuel: familleInfo.reduction_mensuel || 0,
              motif: familleInfo.reduction_motif
            }
          : { inscription: 0, mensuel: 0, motif: null };

      const dataToSend = {
        nom: studentInfo.nom,
        prenom: studentInfo.prenom,
        sexe: studentInfo.sexe,
        dateNaissance: studentInfo.dateNaissance,
        lieuNaissance: studentInfo.lieuNaissance,
        nationalite: studentInfo.nationalite,
        classe_id: studentInfo.classe_id,
        type_inscription: REGISTRATION_TYPE,
        annee_scolaire_id: activeYear.id,

        famille_id: familleInfo.famille_id || null,
        utilise_reduction_famille: familleInfo.appartient_famille ? familleInfo.utilise_reduction_famille : null,
        reduction_custom_inscription: (familleInfo.appartient_famille && !familleInfo.utilise_reduction_famille)
          ? (familleInfo.reduction_inscription || 0)
          : null,
        reduction_custom_mensuel: (familleInfo.appartient_famille && !familleInfo.utilise_reduction_famille)
          ? (familleInfo.reduction_mensuel || 0)
          : null,
        // L'étape famille ne renseigne que `reduction_motif` — `reduction_custom_motif`
        // n'y existe pas et valait donc toujours undefined. Le motif d'une réduction
        // personnalisée n'arrivait jamais jusqu'au serveur : c'est l'une des raisons
        // des 411 réductions sans justification en base.
        reduction_custom_motif: (familleInfo.appartient_famille && !familleInfo.utilise_reduction_famille)
          ? (familleInfo.reduction_motif || null)
          : null,

        reduction_exceptionnelle_inscription: reductionsExceptionnelles.inscription,
        reduction_exceptionnelle_mensuel: reductionsExceptionnelles.mensuel,
        reduction_exceptionnelle_motif: reductionsExceptionnelles.motif,

        politique_ecole: familleInfo.politique_ecole || false,
        politique_type: familleInfo.politique_type || null,
        politique_motif: familleInfo.politique_motif || null,

        parent: {
          nom_prenom: primaryParent.nom_prenom,
          email: primaryParent.email,
          telephone: primaryParent.telephone,
          adresse: primaryParent.adresse
        },
        parents: parentsPayload,
        // Le bus (lot 3a) : posé par le serveur dans la transaction de
        // l'inscription. Rien quand il ne prend pas le bus.
        transport: reglagesTransport?.installe && transportChoix.regime !== 'aucun' ? transportChoix : null,
        finance: {
          inscription: finance.inscription,
          // Le choix fait plus haut est applique ici meme, pas differe : un mois
          // anterieur a l'entree part marque « non scolarise ». Ne rien differer
          // evite d'avoir a memoriser une intention, donc une colonne de plus, et
          // surtout un rappel que quelqu'un devrait penser a traiter.
          paiements_mensuels: (() => {
            const ajuste = { ...finance.paiements_mensuels };

            // La somme saisie avant un plafonnement ne sert qu'a l'avertissement
            // de l'ecran : le serveur n'a que faire de cette note.
            for (const moisId of Object.keys(ajuste)) {
              const { montant_saisi_avant_plafond, ...ligne } = ajuste[moisId] || {};
              ajuste[moisId] = ligne;
            }

            if (traitementMoisAnterieurs === 'non_scolarise' && moisAnterieurs.length > 0) {
              const anterieurs = new Set(moisAnterieurs.map((mois) => String(mois.id)));
              for (const moisId of Object.keys(ajuste)) {
                if (!anterieurs.has(String(moisId))) continue;
                ajuste[moisId] = { ...ajuste[moisId], montant_paye: 0, non_scolarise: true };
              }
            }

            // Le mois d'entree au prorata. On envoie l'intention et la date, pas le
            // montant : c'est le serveur qui calcule, a partir du calendrier de
            // l'ecole. Un montant venu de l'ecran ne serait qu'une proposition.
            if (prorataEntree && traitementMoisEntree === 'prorata') {
              const cle = String(prorataEntree.mois.id);
              if (ajuste[cle]) {
                ajuste[cle] = { ...ajuste[cle], prorata: true, date_entree: dateEntree };
              }
            }

            return ajuste;
          })()
        }
      };

      // 🆕 DEBUG COMPLET AVANT ENVOI
      console.log("=== 2️⃣ DEBUG handleConfirmSubmission (AVANT ENVOI) ===");
      console.log("📦 dataToSend COMPLET:", dataToSend);
      console.log("📦 finance.inscription:", dataToSend.finance.inscription);
      console.log("📦 finance.paiements_mensuels:", dataToSend.finance.paiements_mensuels);
      console.log("📦 Nombre de mois à envoyer:", Object.keys(dataToSend.finance.paiements_mensuels || {}).length);
      console.log("📦 Premier mois (exemple):", dataToSend.finance.paiements_mensuels[Object.keys(dataToSend.finance.paiements_mensuels || {})[0]]);
      console.log("📦 Type de paiements_mensuels:", typeof dataToSend.finance.paiements_mensuels);
      console.log("📦 JSON stringifié:", JSON.stringify(dataToSend.finance.paiements_mensuels).substring(0, 200));
      console.log("======================================================");

      const response = await api.eleves.create(dataToSend);

      console.log('✅ Réponse du serveur:', response);

      if (response && !response.error) {
        // ⚠️ LES PIÈCES MANQUÉES SONT NOMMÉES, PAS AVALÉES.
        // Ce bloc attrapait le jet de `handleFileUploads` dans un `console.error`
        // et annonçait quand même « Inscription réussie » : la secrétaire repartait
        // en croyant le dossier complet, et il fallait rappeler la famille.
        let echecsPieces = [];
        if (response.id) {
          echecsPieces = await handleFileUploads(response.id);
        }

        const identiteEleve = response.matricule
          ? `${studentInfo.prenom} ${studentInfo.nom} - matricule ${response.matricule}`
          : `${studentInfo.prenom} ${studentInfo.nom}`;

        // Le reçu de caisse émis avec l'inscription (aucun sans encaissement).
        const recu = response.recu?.id ? response.recu : null;
        const impressionAuto = Boolean(recu && reglagesImpression.current?.impression_auto);
        let mentionRecu = '';
        if (recu) {
          mentionRecu = impressionAuto
            ? ` - reçu n° ${recu.numero} envoyé à l'imprimante`
            : ` - reçu n° ${recu.numero}, à imprimer depuis le Dossier financier de l'élève`;
        }

        if (echecsPieces.length > 0) {
          // L'élève EST créé : on ne le cache pas, sinon on le ferait créer deux
          // fois. Mais ses pièces ne sont pas enregistrées, et le dire ici est tout
          // le correctif. `long: true` : ce message doit survivre à la remise à zéro
          // du formulaire, un toast de 3 secondes ne suffit pas.
          setToastInfo({
            title: 'Inscription enregistrée, pièces à reprendre',
            message:
              `Inscription réussie : ${identiteEleve}${mentionRecu}.\n\n` +
              `En revanche, ces pièces n'ont PAS été enregistrées : ` +
              `${echecsPieces.map((echec) => echec.libelle).join(', ')}.\n` +
              `Motif : ${echecsPieces[0].raison}.\n\n` +
              `Ouvrez la fiche de l'élève, onglet Documents, pour les joindre.`,
            type: 'warning',
            long: true
          });
        } else {
          setToastInfo({
            message: `Inscription réussie : ${identiteEleve}${mentionRecu}`,
            type: 'success'
          });
        }

        if (impressionAuto) {
          // L'avertissement des pièces manquées reste affiché : l'impression ne
          // le remplace pas, le reçu se réimprime depuis le Dossier financier.
          const garderAvertissement = echecsPieces.length > 0;
          imprimerRecu(recu.id)
            .then(({ message }) => {
              if (message && !garderAvertissement) setToastInfo({ message, type: 'info' });
            })
            .catch((e) => {
              if (garderAvertissement) return;
              setToastInfo({
                message: `${e.message || "Le reçu n'a pas pu être imprimé"}. Il se réimprime depuis le Dossier financier de l'élève.`,
                type: 'error'
              });
            });
        }

        // ⚠️ ON NE QUITTE PAS L'ÉCRAN SUR UN MESSAGE QUE PERSONNE N'A LU.
        // Le toast est rendu dans l'arbre de cet écran, pas dans un portail
        // indépendant : `navigate()` le démonte avec le reste. En mode édition,
        // partir vers la liste détruirait donc l'avertissement « ces pièces n'ont
        // PAS été enregistrées » au moment même où il vient d'apparaître — et le
        // correctif n'aurait rien corrigé sur ce chemin. Quand tout est passé,
        // la navigation reste inchangée.
        if (echecsPieces.length > 0) {
          // Rien d'autre à faire : l'élève est enregistré, le formulaire reste
          // en place le temps que le message soit lu.
        } else if (isEditing) {
          navigate('/liste-eleves');
        } else {
          resetForm();
        }
      } else {
        const apiError = new Error(response.error || response.message || "Erreur lors de l'inscription");
        apiError.code = response.code;
        apiError.status = response.status;
        apiError.data = response.data;
        throw apiError;
      }

    } catch (error) {
      console.error('Erreur detaillee:', error);
      const errorMessage = error.message || "Erreur lors de l'enregistrement";
      setError(errorMessage);
      const isSeuilError = error.code === 'CLASSE_SEUIL_ATTEINT' || /seuil/i.test(errorMessage);
      setToastInfo({
        message: errorMessage,
        type: 'error',
        long: isSeuilError,
        title: isSeuilError ? 'Seuil de classe atteint' : undefined
      });
    } finally {
      confirmSubmissionRef.current = false;
      setLoading(false);
      setShowConfirmation(false);
    }
  };





  // Envoyer les pièces de l'élève, et DIRE lesquelles n'ont pas été enregistrées.
  //
  // ⚠️ AUCUN DES QUATRE RÉSULTATS N'ÉTAIT TESTÉ — celui de la photo était même
  // rangé dans une variable jamais relue. Un refus passait donc inaperçu, et le
  // jet éventuel était avalé par un `console.error` au site d'appel, qui annonçait
  // ensuite « Inscription réussie ». L'élève existait, ses pièces non.
  //
  // Deux règles ici. UN REFUS SUR UNE PIÈCE N'EMPÊCHE PAS D'ESSAYER LES SUIVANTES :
  // on les tente toutes, puis on rend la liste de celles qui ont manqué. Et
  // `success === true` est le SEUL verdict de réussite, parce que `handleResponse`
  // rend un objet `{ success: false, message }` pour 400, 403 et 409 sans lever.
  const handleFileUploads = async (eleveId) => {
    const echecs = [];

    const envoyer = async (libelle, envoi) => {
      try {
        const resultat = await envoi();
        if (!resultat || resultat.success !== true) {
          echecs.push({ libelle, raison: resultat?.message || resultat?.error || 'refus du serveur' });
        }
      } catch (error) {
        echecs.push({ libelle, raison: error?.message || 'erreur réseau' });
      }
    };

    if (photoFile) {
      const photoFormData = new FormData();
      photoFormData.append('photo', photoFile);
      await envoyer('la photo', () => api.eleves.uploadPhoto(eleveId, photoFormData));
    }

    if (identityDoc) {
      const identityFormData = new FormData();
      identityFormData.append('document', identityDoc);
      await envoyer("la pièce d'identité", () => api.eleves.uploadIdentityDoc(eleveId, identityFormData));
    }

    if (diplomas.length > 0) {
      const diplomasFormData = new FormData();
      diplomas.forEach((file) => diplomasFormData.append('documents', file));
      await envoyer(
        diplomas.length > 1 ? `les ${diplomas.length} diplômes` : 'le diplôme',
        () => api.eleves.uploadDiplomas(eleveId, diplomasFormData)
      );
    }

    if (otherDocs.length > 0) {
      const otherDocsFormData = new FormData();
      otherDocs.forEach((file) => otherDocsFormData.append('documents', file));
      await envoyer(
        otherDocs.length > 1 ? `les ${otherDocs.length} autres documents` : "l'autre document",
        () => api.eleves.uploadOtherDocs(eleveId, otherDocsFormData)
      );
    }

    return echecs;
  };


  const resetForm = () => {
    if (isEditing) {
      localStorage.removeItem('editingEleveId');
      navigate('/liste-eleves');
      return;
    }

    setStudentInfo({
      nom: '',
      prenom: '',
      sexe: '',
      dateNaissance: '',
      lieuNaissance: '',
      nationalite: '',
      classe_id: '',
      annee_scolaire_id: activeYear ? activeYear.id : null
    });

    setParentInfo({
      nom_prenom: '',
      email: '',
      telephone: '',
      adresse: ''
    });
    setParentMode('single');
    setParentsInfo([{ ...DEFAULT_PARENT }]);

    // ?? AJOUTÃ: RÃ©initialiser les infos famille
    // 🆕 Réinitialiser les infos famille
    setFamilleInfo({
      appartient_famille: false,
      famille_id: null,
      famille_nom: '',
      reduction_inscription: 0,
      reduction_mensuel: 0,
      reduction_exceptionnelle: false,
      reduction_motif: '',
      utilise_reduction_famille: true, // 🆕
      reduction_custom_motif: '' // 🆕
    });

    // RÃ©initialiser la finance avec les mois scolaires chargÃ©s
    const paiementsMensuels = {};
    moisScolaires.forEach(mois => {
      paiementsMensuels[mois.id] = {
        mois_id: mois.id,
        mois_nom: mois.nom,
        trimestre: mois.trimestre,
        montant_attendu: 0,
        montant_paye: 0,
        paye: false,
        statut: 'Non payé'
      };
    });

    setFinance({
      inscription: { montant: 0, paye: false, statut: 'Non payé' },
      paiements_mensuels: paiementsMensuels
    });
    // La date d'entree, ses choix, les avertissements et le montant de reference
    // de l'etape 4 sont ceux de l'eleve qui vient d'etre inscrit : le suivant
    // repart sans eux. Garder la date ferait partir ses mois anterieurs en « non
    // scolarise » a 0 FCFA et son mois d'entree au prorata, sans qu'on l'ait
    // saisie pour lui.
    setDateEntree('');
    setTraitementMoisAnterieurs('non_scolarise');
    setTraitementMoisEntree('prorata');
    setAjustementsMois([]);
    montantInscriptionEtape4Ref.current = null;
    // Le bus de l'élève inscrit ne vaut pas pour le suivant.
    setTransportChoix(SANS_BUS);

    setCurrentStep(1);
    setPhotoFile(null);
    setPhotoPreview(null);
    setIdentityDoc(null);
    setDiplomas([]);
    setOtherDocs([]);
    setClasseSelectionnee(null);
    setClassCapacity(null);
    setClassCapacityLoading(false);
    stopCamera();
  };




  const renderStep1 = () => (
    <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="CREATE">
      <div className="space-y-6 min-h-[560px] pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-700">Nom</label>
            <input
              type="text"
              value={studentInfo.nom}
              onChange={(e) => setStudentInfo(prev => ({ ...prev, nom: e.target.value }))}
              className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">Prénom</label>
            <input
              type="text"
              value={studentInfo.prenom}
              onChange={(e) => setStudentInfo(prev => ({ ...prev, prenom: e.target.value }))}
              className="w-full px-4 py-2.5 mt-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-700">Sexe</label>
            <SearchableDropdown
              value={studentInfo.sexe}
              options={sexOptions}
              onChange={(option) => setStudentInfo(prev => ({ ...prev, sexe: option.value }))}
              placeholder="Sélectionner"
              searchable={false}
            />
            <input type="hidden" value={studentInfo.sexe} required readOnly />
          </div>

          <div>
            <label className="text-sm text-gray-700">Date de naissance</label>
            <div className="relative mt-1">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="date"
                value={studentInfo.dateNaissance}
                onChange={(e) => setStudentInfo(prev => ({ ...prev, dateNaissance: e.target.value }))}
                className="w-full pl-10 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-700">Lieu de naissance</label>
            <SearchableDropdown
              options={countryOptions}
              value={studentInfo.lieuNaissance}
              onChange={(option) => setStudentInfo(prev => ({ ...prev, lieuNaissance: option.value }))}
              placeholder="Sélectionner un pays"
            />
            <input type="hidden" value={studentInfo.lieuNaissance} required readOnly />
          </div>

          <div>
            <label className="text-sm text-gray-700">Nationalité</label>
            <SearchableDropdown
              options={countryOptions}
              value={studentInfo.nationalite}
              onChange={(option) => setStudentInfo(prev => ({ ...prev, nationalite: option.value }))}
              placeholder="Sélectionner une nationalité"
            />
            <input type="hidden" value={studentInfo.nationalite} required readOnly />
          </div>

          <div>
            <label className="text-sm text-gray-700">Classe</label>
            <GroupedClassDropdown
              value={studentInfo.classe_id}
              classes={classes}
              sections={sections}
              onChange={handleClasseChange}
              disabled={isEditing}
            />
            <ClassCapacityInfo capacity={classCapacity} loading={classCapacityLoading} />
            <input type="hidden" value={studentInfo.classe_id} required readOnly />
          </div>
        </div>
      </div>
    </ProtectedComponent>
  );


  const renderStep2Famille = () => (
    <FamilleNombreuseStep
      familleInfo={familleInfo}
      setFamilleInfo={setFamilleInfo}
      classeSelectionnee={classeSelectionnee}
      onValidate={() => {
        // Validation automatique si "Non" sélectionné
        if (!familleInfo.appartient_famille) {
          setCurrentStep(3);
        }
      }}
    />
  );


  const renderStep3 = () => (
    <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="CREATE">
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2 rounded-lg bg-gray-50 border border-gray-200 p-1">
          <button
            type="button"
            onClick={() => handleParentModeChange('single')}
            className={`flex-1 min-w-[180px] px-4 py-2 rounded-md text-sm font-medium transition-colors ${parentMode === 'single'
                ? 'bg-white text-[#246990] shadow-sm border border-[#b8d4e2]'
                : 'text-gray-600 hover:text-[#246990]'
              }`}
          >
            Un parent / tuteur
          </button>
          <button
            type="button"
            onClick={() => handleParentModeChange('double')}
            className={`flex-1 min-w-[180px] px-4 py-2 rounded-md text-sm font-medium transition-colors ${parentMode === 'double'
                ? 'bg-white text-[#246990] shadow-sm border border-[#b8d4e2]'
                : 'text-gray-600 hover:text-[#246990]'
              }`}
          >
            Deux parents
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {parentsInfo.map((parent, parentIndex) => (
            <div key={parentIndex} className="rounded-lg border border-gray-200 bg-white overflow-visible">
              <div className="px-4 py-3 border-b border-gray-100 bg-[#f4fafc] flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#e7f3f8] text-[#246990] flex items-center justify-center">
                    <UserRound size={18} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Responsable {parentIndex + 1}</p>
                    <p className="text-xs text-gray-500">Coordonnées séparées et exploitables</p>
                  </div>
                </div>
                {parent.principal && (
                  <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold border border-[#b8d4e2]">Principal</span>
                )}
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="text-sm text-gray-700">Lien avec l'élève</label>
                  <SearchableDropdown
                    value={parent.relation}
                    options={parentRelationOptions}
                    onChange={(option) => handleParentChange(parentIndex, 'relation', option.value)}
                    searchable={false}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-700">Nom et prénom</label>
                  <div className="relative mt-1">
                    <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={parent.nom_prenom}
                      onChange={(e) => handleParentChange(parentIndex, 'nom_prenom', e.target.value)}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-700">Email</label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      value={parent.email}
                      onChange={(e) => handleParentChange(parentIndex, 'email', e.target.value)}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm text-gray-700">Téléphone</label>
                    {(parent.telephones || []).length < 2 && (
                      <button
                        type="button"
                        onClick={() => addParentPhone(parentIndex)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#b8d4e2] bg-[#e7f3f8] text-[#246990] text-xs font-medium"
                      >
                        <Plus size={13} />
                        Ajouter un numéro
                      </button>
                    )}
                  </div>

                  {(parent.telephones || ['']).map((phone, phoneIndex) => (
                    <div key={phoneIndex} className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => handleParentPhoneChange(parentIndex, phoneIndex, e.target.value)}
                        placeholder={phoneIndex === 0 ? 'Numéro principal' : 'Numéro secondaire'}
                        className="w-full pl-10 pr-10 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                        required={phoneIndex === 0}
                      />
                      {phoneIndex > 0 && (
                        <button
                          type="button"
                          onClick={() => removeParentPhone(parentIndex, phoneIndex)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-red-500 hover:bg-red-50"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">Saisir chaque numéro dans un champ séparé, sans slash.</p>
                </div>

                <div>
                  <label className="text-sm text-gray-700">Adresse</label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-3 text-gray-400" size={20} />
                    <textarea
                      value={parent.adresse}
                      onChange={(e) => handleParentChange(parentIndex, 'adresse', e.target.value)}
                      rows={3}
                      className="w-full pl-10 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ProtectedComponent>
  );


  // FCFA sans decimales, separateur de milliers insecable — convention maison.
  const formaterMontant = (valeur) =>
    `${Math.round(Number(valeur) || 0).toLocaleString('fr-FR').replace(/ | /g, ' ')} FCFA`;

  /**
   * L'étape des frais, pour une école sans comptabilité.
   *
   * Elle annonce le tarif de la classe et le coût de l'année — l'information
   * qu'un parent demande — sans offrir aucun point de saisie. Rien n'est coché,
   * rien n'est encaissé : ces gestes appartiennent aux modules financiers.
   */
  const renderStep4LectureSeule = () => {
    const mois = Object.values(finance.paiements_mensuels || {});
    const fraisInscription = Number(finance.inscription?.montant) || 0;
    const totalMensuel = mois.reduce((somme, m) => somme + (Number(m.montant_attendu) || 0), 0);
    // Les mensualités sont presque toujours identiques ; on affiche la valeur
    // représentative plutôt qu'une moyenne, qui inventerait un montant que
    // personne ne paie.
    const mensualite = mois.length ? Number(mois[0].montant_attendu) || 0 : 0;
    const mensualitesEgales = mois.every(
      (m) => (Number(m.montant_attendu) || 0) === mensualite
    );
    const totalAnnuel = fraisInscription + totalMensuel;

    const Ligne = ({ libelle, detail, montant, fort = false }) => (
      <div className="flex items-baseline justify-between gap-4 py-3">
        <div>
          <div className={fort ? 'font-semibold text-gray-900' : 'text-gray-700'}>{libelle}</div>
          {detail && <div className="text-xs text-gray-500 mt-0.5">{detail}</div>}
        </div>
        <div
          className={`shrink-0 tabular-nums ${
            fort ? 'text-xl font-bold text-[#246990]' : 'text-lg font-semibold text-gray-900'
          }`}
        >
          {formaterMontant(montant)}
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Frais de scolarité</h3>
          <p className="text-sm text-gray-500">
            Tarif de la classe {classeSelectionnee?.nom ? `« ${classeSelectionnee.nom} »` : 'sélectionnée'}
            {activeYear?.nom ? ` pour l'année ${activeYear.nom}` : ''}.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 px-4">
          <Ligne libelle="Frais d'inscription" montant={fraisInscription} />
          <Ligne
            libelle="Frais mensuels"
            detail={
              mensualitesEgales
                ? `${mois.length} mois sur l'année scolaire`
                : `${mois.length} mois, montants variables selon le calendrier`
            }
            montant={mensualitesEgales ? mensualite : totalMensuel}
          />
          <Ligne
            libelle="Total pour l'année"
            detail="Inscription et mensualités cumulées"
            montant={totalAnnuel}
            fort
          />
        </div>

        {comptabiliteOuverte ? (
          <BandeauCaisseReservee />
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            <p className="leading-relaxed">
              L'encaissement des frais relève des modules de gestion financière, non inclus
              dans la licence de l'établissement. L'échéancier de l'élève est néanmoins créé
              et conservé : il deviendra utilisable tel quel si la licence évolue.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Le tarif de bus de la classe choisie. Une classe sans tarif n'ouvre pas le
  // bus, sauf quand l'école a des zones de trajet (le prix suit alors la zone,
  // lot 3a bis) : un choix fait pour une autre classe est retiré, sinon le
  // serveur refuserait l'inscription entière.
  const classeDuBus = classes.find((classe) => String(classe.id) === String(studentInfo.classe_id)) || classeSelectionnee || null;
  const tarifBus = Math.round(Number(classeDuBus?.frais_transport) || 0);
  const busOuvert = (reglagesTransport?.zones?.length || 0) > 0 || tarifBus > 0;
  useEffect(() => {
    if (!busOuvert && transportChoix.regime !== 'aucun') setTransportChoix(SANS_BUS);
  }, [busOuvert, transportChoix.regime]);

  const renderTransport = () => (reglagesTransport?.installe ? (
    <ChoixTransport
      tarif={tarifBus}
      reglages={reglagesTransport}
      mois={moisScolaires}
      classe={classeDuBus?.nom || null}
      valeur={transportChoix}
      onChange={setTransportChoix}
    />
  ) : null);

  const renderStep4 = () => {
    if (isLoadingMois) {
      return (
        <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="MANAGE_FINANCE">
          <div className="flex justify-center items-center h-40">
            <div className="text-gray-500">Chargement des mois scolaires...</div>
          </div>
        </ProtectedComponent>
      );
    }

    if (moisScolaires.length === 0) {
      return (
        <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="MANAGE_FINANCE">
          <div className="flex justify-center items-center h-40">
            <div className="text-red-500">Aucun mois scolaire configuré pour cette année</div>
          </div>
        </ProtectedComponent>
      );
    }

    const trimestres = getMoisParTrimestre();

    // ------------------------------------------------------------------
    // Sans comptabilité : la même étape, mais en lecture
    // ------------------------------------------------------------------
    // On ne masque pas l'étape — dans un parcours numéroté, une étape absente
    // casse la numérotation et laisse l'utilisateur devant un trou. Elle cesse
    // simplement d'être un point de saisie et se contente d'annoncer le tarif.
    //
    // L'échéancier est créé quand même, mois par mois, tout à zéro payé : la
    // licence gouverne ce que l'école peut faire, jamais ce que KLASYS
    // enregistre. C'est ce qui rend l'upgrade indolore — le grand livre est
    // déjà là le jour où l'école accède à la comptabilité.
    // Le choix du bus n'encaisse rien : il vaut dans les deux formes de l'étape.
    if (!comptabiliteOuverte || !habiliteCaisse) {
      return (
        <div className="space-y-6">
          {renderStep4LectureSeule()}
          {renderTransport()}
        </div>
      );
    }

    return (
      <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="MANAGE_FINANCE">
        <div className="space-y-6">
          {renderTransport()}
          {/* ?? AFFICHER INFO RÃDUCTION (FAMILLE OU EXCEPTIONNELLE) */}



          {/* Rappel de la reduction qui s'applique a cet eleve.
            Les trois cas - famille, politique d'ecole, exceptionnelle - passent
            par la meme structure : c'est ce qui garantit que les montants
            tombent dans la meme colonne. Avant, chaque cas avait sa liste a
            puces et ses couleurs, et les chiffres se decalaient selon la
            longueur du libelle. */}
          {(familleInfo.appartient_famille || familleInfo.reduction_exceptionnelle || (familleInfo.politique_ecole && familleInfo.politique_type)) && (() => {
            const source = familleInfo.politique_ecole
              ? {
                Icone: Award,
                titre: "Politique d'école",
                detail: LIBELLES_POLITIQUE[familleInfo.politique_type] || "Politique d'école"
              }
              : familleInfo.appartient_famille
                ? { Icone: Users, titre: 'Famille nombreuse', detail: familleInfo.famille_nom }
                : { Icone: Percent, titre: 'Réduction exceptionnelle', detail: null };

            const { Icone } = source;
            const reductionMensuelle = Number(familleInfo.reduction_mensuel) || 0;
            const reductionInscription = Number(familleInfo.reduction_inscription) || 0;

            return (
              <div className="rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#e7f3f8] text-[#246990]">
                    <Icone size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{source.titre}</p>
                    {source.detail && (
                      <p className="truncate text-xs text-gray-600">{source.detail}</p>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 border-t border-[#d7e8f0] pt-3 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Réduction inscription
                    </span>
                    {familleInfo.politique_ecole ? (
                      <p className="mt-1 font-semibold text-[#246990]">Inscription offerte</p>
                    ) : reductionInscription > 0 ? (
                      <p className="mt-1 font-semibold text-[#246990]">{fcfa(reductionInscription)}</p>
                    ) : (
                      <p className="mt-1 font-semibold text-gray-400">Aucune</p>
                    )}
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Réduction mensuelle
                    </span>
                    {reductionMensuelle > 0 ? (
                      <p className="mt-1 font-semibold text-[#246990]">{fcfa(reductionMensuelle)}</p>
                    ) : (
                      <p className="mt-1 font-semibold text-gray-400">Aucune</p>
                    )}
                  </div>
                </div>

                {familleInfo.politique_ecole && reductionMensuelle === 0 && (
                  <p className="mt-3 text-[11px] leading-snug text-gray-600">
                    Les frais mensuels restent au tarif normal de la classe : cette politique ne porte
                    que sur l'inscription.
                  </p>
                )}

                {!familleInfo.politique_ecole && !familleInfo.appartient_famille && familleInfo.reduction_motif && (
                  <p className="mt-3 text-[11px] leading-snug text-gray-600">
                    Motif : {familleInfo.reduction_motif}
                  </p>
                )}
              </div>
            );
          })()}


          {/* Date d'entree a l'ecole.
            Un eleve inscrit en janvier n'a pas frequente octobre a decembre. Sans
            ce champ, le parcours creait neuf mois dus, et dans une famille dont
            les mois etaient deja regles, leur total montait d'un coup et tous
            basculaient en « partiel » - sans que rien ne l'annonce. */}
          <div className="mb-6 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-800">
                Cet élève fréquente l'école à partir du
              </span>
              <input
                type="date"
                value={dateEntree}
                onChange={(e) => setDateEntree(e.target.value)}
                className="mt-2 h-9 w-full max-w-xs rounded-lg border border-gray-200 px-3 focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
              />
              <span className="mt-1 block text-[11px] text-gray-600">
                {moisEntree
                  ? `Soit le mois de ${moisEntree.nom}.`
                  : "Laissez vide s'il est présent depuis le début de l'année."}
              </span>
            </label>

            {/* Le mois d'entree lui-meme. Un eleve arrive le 15 : il n'a pas
              frequente le mois entier, mais il n'est pas non plus « non
              scolarise ». Sans ce choix, la caisse encaissait la moitie du
              tarif et le mois restait marque « Partiel » - donc compte comme
              une dette qui n'existe pas. */}
            {prorataEntree && (
              <div className="mt-4 border-t border-[#cfe3ec] pt-4">
                <p className="text-sm font-medium text-gray-800">
                  Il arrive en cours de {prorataEntree.mois.nom} - {prorataEntree.joursPresents} jour
                  {prorataEntree.joursPresents > 1 ? 's' : ''} sur {prorataEntree.joursDuMois}. Que doit-il pour ce mois ?
                </p>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                  <CarteChoix
                    actif={traitementMoisEntree === 'prorata'}
                    onClick={() => setTraitementMoisEntree('prorata')}
                    titre={`Le prorata - environ ${fcfa(prorataEntree.montantProrata)}`}
                    description={`Au lieu de ${fcfa(prorataEntree.montantPlein)}. Ce montant est intégralement dû : une fois réglé, le mois est soldé, pas partiel.`}
                  >
                    <CalculProrata
                      montantPlein={prorataEntree.montantPlein}
                      joursPresents={prorataEntree.joursPresents}
                      joursDuMois={prorataEntree.joursDuMois}
                      montantProrata={prorataEntree.montantProrata}
                    />
                  </CarteChoix>
                  <CarteChoix
                    actif={traitementMoisEntree === 'plein'}
                    onClick={() => setTraitementMoisEntree('plein')}
                    titre={`Le tarif normal - ${fcfa(prorataEntree.montantPlein)}`}
                    description="Le mois est dû en entier, comme les autres."
                  />
                </div>
              </div>
            )}

            {moisAnterieurs.length > 0 && (
              <div className="mt-4 border-t border-[#cfe3ec] pt-4">
                <p className="text-sm font-medium text-gray-800">
                  {moisAnterieurs.length === 1
                    ? `${moisAnterieurs[0].nom} est antérieur à son entrée. Que doit-il pour ce mois ?`
                    : `${moisAnterieurs.map((m) => m.nom).join(', ')} sont antérieurs à son entrée. Que doit-il pour ces mois ?`}
                </p>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                  <CarteChoix
                    actif={traitementMoisAnterieurs === 'non_scolarise'}
                    onClick={() => setTraitementMoisAnterieurs('non_scolarise')}
                    titre="Rien - il n'était pas encore à l'école"
                    description={`${moisAnterieurs.length === 1 ? 'Le mois sera créé' : 'Les mois seront créés'} en « non scolarisé », à 0 FCFA.`}
                  />
                  <CarteChoix
                    actif={traitementMoisAnterieurs === 'du'}
                    onClick={() => setTraitementMoisAnterieurs('du')}
                    titre="Le tarif normal - inscription tardive à régulariser"
                    description={`${moisAnterieurs.length === 1 ? 'Le mois sera dû' : 'Les mois seront dus'} comme les autres.`}
                  />
                </div>

                {/* L'avertissement ne s'affiche que s'il y a vraiment quelque chose a
                  bouleverser : une famille dont ces mois sont deja regles. */}
                {traitementMoisAnterieurs === 'du' && moisAnterieursRegles.length > 0 && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
                    <span className="font-semibold">Cette famille a déjà réglé {moisAnterieursRegles.map((m) => m.nom).join(', ')}.</span>
                    {' '}Faire payer {moisAnterieursRegles.length === 1 ? 'ce mois' : 'ces mois'} à cet élève en augmentera le total
                    et {moisAnterieursRegles.length === 1 ? 'le fera' : 'les fera'} passer en « partiel ».
                    Les règlements déjà encaissés ne sont pas modifiés.
                  </div>
                )}
              </div>
            )}

            {/* Les sommes deja saisies que la date d'entree vient de rendre
              caduques. Le dire, plutot que de les laisser affichees puis de les
              corriger silencieusement a l'envoi. */}
            {ajustementsMois.some((m) => m.type === 'efface' || m.type === 'plafonne') && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span className="space-y-1">
                  {ajustementsMois.filter((m) => m.type === 'efface').length > 0 && (
                    <span className="block">
                      <span className="font-semibold">
                        {(() => {
                          const effaces = ajustementsMois.filter((m) => m.type === 'efface');
                          return effaces.length === 1
                            ? `Le montant saisi sur ${effaces[0].nom} a été remis à zéro.`
                            : `Les montants saisis sur ${effaces.map((m) => m.nom).join(', ')} ont été remis à zéro.`;
                        })()}
                      </span>
                      {' '}Ces mois avaient été déclarés non scolarisés : il n'y avait rien à y encaisser.
                      Ressaisissez le montant si vous revenez sur ce choix.
                    </span>
                  )}
                  {ajustementsMois.filter((m) => m.type === 'plafonne').map((mois) => (
                    <span key={mois.id} className="block">
                      <span className="font-semibold">
                        Le montant saisi sur {mois.nom} a été ramené à {fcfa(mois.montant)}.
                      </span>
                      {' '}Ce mois est au prorata : encaisser davantage créerait un trop-perçu.
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>

          {/* Les cases « Payé » decochees parce que le montant du de la ligne a
            change depuis qu'elle avait ete cochee (classe, reduction, date
            d'entree ou choix du prorata). Le dire juste au-dessus des lignes,
            plutot que de laisser partir un encaissement qui n'est plus le bon. */}
          {ajustementsMois.some((m) => m.type === 'decoche') && (() => {
            const decochees = ajustementsMois.filter((m) => m.type === 'decoche');
            const inscription = decochees.find((m) => m.id === 'inscription');
            // Une inscription devenue offerte n'a plus de case : la consigne
            // « cochez à nouveau » ne s'y applique pas, la somme est a rendre.
            const inscriptionOfferte = Boolean(inscription)
              && Number(finance.inscription?.montant_final ?? finance.inscription?.montant ?? 0) <= 0;
            // Ce que le mois doit aujourd'hui, comme le tableau ci-dessous le lit.
            const duDuMois = (id) => {
              const etat = etatsParMois.get(id);
              return etat ? etat.montantDu : (Number(finance.paiements_mensuels?.[id]?.montant_attendu) || 0);
            };
            // Les mois, dans l'ordre du calendrier de l'ecole ; ceux decoches pour
            // une meme somme, et dans le meme cas, tiennent sur une seule ligne.
            const rang = new Map(moisScolaires.map((mois, index) => [String(mois.id), index]));
            const groupes = new Map();
            decochees
              .filter((m) => m.id !== 'inscription')
              .sort((a, b) => (rang.get(a.id) ?? 0) - (rang.get(b.id) ?? 0))
              .forEach((m) => {
                const rienDu = duDuMois(m.id) <= 0;
                const cle = `${m.montant}|${m.saisie ? 'saisie' : 'coche'}|${rienDu ? 'rien' : 'du'}`;
                const groupe = groupes.get(cle) || { montant: m.montant, saisie: Boolean(m.saisie), rienDu, noms: [] };
                groupe.noms.push(m.nom);
                groupes.set(cle, groupe);
              });
            const aRecocher = (Boolean(inscription) && !inscriptionOfferte)
              || [...groupes.values()].some((groupe) => !groupe.rienDu);

            // Cochee par la caisse : la somme pour laquelle elle l'a ete. Cochee
            // d'office par un plafonnement : la somme que la caisse avait saisie.
            const libelleMois = (groupe) => {
              const plusieurs = groupe.noms.length > 1;
              const somme = groupe.saisie
                ? `${fcfa(groupe.montant)} saisis${plusieurs ? ' sur chacun' : ''}`
                : `${plusieurs ? 'cochés' : 'coché'} pour ${fcfa(groupe.montant)}${plusieurs ? ' chacun' : ''}`;
              const suite = groupe.rienDu
                ? ", plus rien n'est dû : la somme encaissée est à rendre à la famille"
                : '';
              return `${groupe.noms.join(', ')} - ${somme}${suite}`;
            };

            return (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-snug text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="space-y-1.5">
                  <p className="font-semibold">
                    {decochees.length > 1
                      ? 'Ces cases « Payé » ont été décochées : leur montant dû a changé.'
                      : 'Cette case « Payé » a été décochée : son montant dû a changé.'}
                  </p>
                  <ul className="space-y-0.5">
                    {inscription && (
                      <li>
                        {inscriptionOfferte
                          ? `Inscription - cochée pour ${fcfa(inscription.montant)}, désormais offerte : la somme encaissée est à rendre à la famille`
                          : `Inscription - cochée pour ${fcfa(inscription.montant)}`}
                      </li>
                    )}
                    {[...groupes.entries()].map(([cle, groupe]) => (
                      <li key={cle}>{libelleMois(groupe)}</li>
                    ))}
                  </ul>
                  <p>
                    La classe, la réduction ou la date d'entrée a été modifiée depuis.
                    {aRecocher && ' Vérifiez la somme réellement encaissée, puis cochez à nouveau ce qui est réglé.'}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Frais d'inscription */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Frais d'inscription</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{activeYear?.nom}</span>
                <div className="flex items-center gap-4">
                  {/* ?? Afficher la rÃ©duction si famille OU exceptionnelle */}


                  {/* 🆕 Afficher la réduction si politique OU famille OU exceptionnelle */}

                  {((familleInfo.politique_ecole && familleInfo.politique_type) ||
                    (familleInfo.appartient_famille && (familleInfo.reduction_inscription > 0 || familleInfo.reduction_mensuel > 0)) ||
                    (familleInfo.reduction_exceptionnelle && (familleInfo.reduction_inscription > 0 || familleInfo.reduction_mensuel > 0))) ? (
                    <div className="text-right">
                      <div className="text-sm text-gray-500 line-through">
                        {(finance.inscription.montant_normal || 0).toLocaleString()} FCFA
                      </div>

                      <div className="text-sm text-green-600">
                        Réduction : -{(finance.inscription.montant_reduction || 0).toLocaleString()} FCFA
                        {finance.inscription.montant_normal > 0 && finance.inscription.montant_reduction > 0 && (
                          <span className="ml-1">
                            ({((finance.inscription.montant_reduction / finance.inscription.montant_normal) * 100).toFixed(2)}%)
                          </span>
                        )}
                      </div>

                      <div className="font-bold text-lg text-green-700">
                        A payer: {(finance.inscription.montant_final || 0).toLocaleString()} FCFA
                      </div>
                    </div>
                  ) : (
                    <span className="font-medium">{(finance.inscription.montant || 0).toLocaleString()} FCFA</span>
                  )}
                  {/* Quand la réduction couvre la totalité des frais, il n'y a rien
                    à encaisser : la case laissée décochée donnait à croire qu'un
                    règlement restait dû. Le serveur enregistre déjà la ligne comme
                    réglée dans ce cas ; l'écran doit dire la même chose. */}
                  {Number(finance.inscription.montant_final ?? finance.inscription.montant ?? 0) <= 0 ? (
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-sm font-medium text-emerald-800">
                      <Check className="w-4 h-4" />
                      Offerte - rien à encaisser
                    </span>
                  ) : (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={finance.inscription.paye}
                        onChange={(e) => handleCheckboxChange(e, 'inscription')}
                        className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                      />
                      <span className="text-sm text-gray-600">Payé</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Frais mensuels par trimestre */}
          {Object.keys(trimestres).map(trimestre => (
            <div key={trimestre}>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {trimestre === '1' ? 'Premier trimestre' :
                  trimestre === '2' ? 'Deuxième trimestre' :
                    trimestre === '3' ? 'Troisième trimestre' : `Trimestre ${trimestre}`}
              </h3>
              <div className="space-y-4">
                {trimestres[trimestre].map(mois => {
                  const moisData = finance.paiements_mensuels[mois.id] || {};

                  // Le mois obeit au choix fait plus haut : non scolarise, au
                  // prorata, ou plein. Sans cette lecture, l'ecran invitait encore
                  // a encaisser un mois declare non scolarise juste au-dessus.
                  const etat = etatsParMois.get(String(mois.id)) || null;
                  const nonScolarise = etat?.type === 'non_scolarise';
                  const auProrata = etat?.type === 'prorata';
                  const montantDu = etat ? etat.montantDu : (Number(moisData.montant_attendu) || 0);
                  const resteAPayer = montantDu - (Number(moisData.montant_paye) || 0);

                  return (
                    <div
                      key={mois.id}
                      className={`p-4 rounded-lg border ${nonScolarise
                        ? 'border-gray-200 bg-gray-50'
                        : auProrata
                          ? 'border-[#cfe3ec] bg-[#f6fbfd]'
                          : 'bg-white border-gray-200'
                        }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className={`inline-block w-24 flex-shrink-0 font-medium ${nonScolarise ? 'text-gray-400' : 'text-gray-700'}`}>
                            {mois.nom}
                          </span>
                          {nonScolarise && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                              <CalendarOff className="h-3 w-3" />
                              Non scolarisé - 0 FCFA
                            </span>
                          )}
                          {auProrata && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-[#cfe3ec] bg-[#eef7fb] px-2 py-0.5 text-[11px] font-semibold text-[#246990]">
                              <Calculator className="h-3 w-3" />
                              Prorata - {fcfa(montantDu)} au lieu de {fcfa(etat.montantPlein)}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          {nonScolarise ? (
                            <span className="text-[11px] italic text-gray-500">
                              Rien à encaisser sur ce mois
                            </span>
                          ) : (
                            <>
                              {!moisData.paye && (
                                <div className="relative">
                                  <input
                                    type="number"
                                    value={moisData.montant_paye || ''}
                                    onChange={(e) => handleMoisPaiement(mois.id, 'montant_paye', e.target.value, montantDu)}
                                    className="w-32 px-4 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                                    placeholder="0"
                                    min="0"
                                    max={montantDu}
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                    FCFA
                                  </span>
                                </div>
                              )}
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={moisData.paye || false}
                                  onChange={(e) => handleMoisPaiement(mois.id, 'paye', e.target.checked, montantDu)}
                                  className="rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                                />
                                <span className="text-sm text-gray-600">Payé</span>
                              </label>
                            </>
                          )}

                          <div className="text-right min-w-[180px]">
                            {nonScolarise ? (
                              <span className="text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-500">
                                0 FCFA
                              </span>
                            ) : auProrata ? (
                              <>
                                <div className="text-xs text-gray-500 line-through">
                                  Tarif plein : {fcfa(etat.montantPlein)}
                                </div>
                                <div className="text-xs text-[#246990]">
                                  {etat.joursPresents} jour{etat.joursPresents > 1 ? 's' : ''} sur {etat.joursDuMois}
                                </div>
                                <div className="text-sm font-bold text-[#246990] mt-1">
                                  A payer: {fcfa(montantDu)}
                                </div>
                              </>
                            ) : ((familleInfo.politique_ecole && familleInfo.politique_type) ||
                              (familleInfo.appartient_famille && (familleInfo.reduction_inscription > 0 || familleInfo.reduction_mensuel > 0)) ||
                              (familleInfo.reduction_exceptionnelle && (familleInfo.reduction_inscription > 0 || familleInfo.reduction_mensuel > 0))) ? (
                              <>
                                <div className="text-xs text-gray-500 line-through">
                                  Normal: {(moisData.montant_normal || 0).toLocaleString()} FCFA
                                </div>
                                <div className="text-xs text-green-600">
                                  Réduction : -{(moisData.montant_reduction || 0).toLocaleString()} FCFA
                                  {moisData.montant_normal > 0 && moisData.montant_reduction > 0 && (
                                    <span className="ml-1">
                                      ({((moisData.montant_reduction / moisData.montant_normal) * 100).toFixed(2)}%)
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm font-bold text-green-700 mt-1">
                                  A payer: {(moisData.montant_final || 0).toLocaleString()} FCFA
                                </div>
                              </>
                            ) : (
                              <span className={`text-sm px-3 py-1 rounded-full ${moisData.paye ? 'bg-green-100 text-green-800' :
                                  (moisData.montant_paye > 0) ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {fcfa(montantDu)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {!nonScolarise && moisData.montant_paye > 0 && resteAPayer > 0 && (
                        <div className="mt-2 text-sm text-blue-600">
                          Reste à payer : {fcfa(resteAPayer)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ProtectedComponent>
    );
  };


  const renderDocumentList = (files, onRemove) => (
    <div className="mt-3 space-y-2">
      {files.map((file, index) => (
        <div key={index} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-md bg-[#e7f3f8] text-[#246990] flex items-center justify-center flex-shrink-0">
              <FileText size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <button type="button" onClick={() => onRemove(index)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50 flex-shrink-0" title="Retirer le document">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );

  const renderStep5 = () => (
    <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="UPLOAD_DOCUMENTS">
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-[#f4fafc] flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#e7f3f8] text-[#246990] flex items-center justify-center">
                <ImageIcon size={18} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Photo de l'élève</p>
                <p className="text-xs text-gray-500">Importer une photo ou la prendre directement.</p>
              </div>
            </div>

            <div className="p-4">
              {cameraActive ? (
                <div className="space-y-3">
                  <div className="mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-lg bg-black border border-gray-200">
                    <video ref={videoRef} autoPlay playsInline className="h-full w-full object-cover" />
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Cadrez le visage au centre du carré avant de capturer.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={handleCapturePhoto} className="px-4 py-2 rounded-lg bg-[#246990] text-white text-sm font-medium hover:bg-[#3084b5]">Capturer</button>
                    <button type="button" onClick={stopCamera} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50">Annuler</button>
                  </div>
                </div>
              ) : photoPreview ? (
                <div className="relative mx-auto aspect-square w-full max-w-sm rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <img src={photoPreview} alt="Aperçu" className="h-full w-full object-cover rounded-md" />
                  <button type="button" onClick={clearPhoto} className="absolute top-5 right-5 p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
                  <Upload className="mx-auto text-gray-400" size={34} />
                  <p className="mt-3 text-sm font-medium text-gray-700">Aucune photo sélectionnée</p>
                  <p className="text-xs text-gray-500">PNG ou JPG jusqu'à 5MB</p>
                </div>
              )}

              {cameraError && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{cameraError}</div>
              )}

              {!cameraActive && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <label htmlFor="photo" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#246990] text-white text-sm font-medium hover:bg-[#3084b5] cursor-pointer">
                    <Upload size={16} />
                    Importer
                    <input id="photo" type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                  </label>
                  <button type="button" onClick={startCamera} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#b8d4e2] bg-[#e7f3f8] text-[#246990] text-sm font-medium hover:bg-[#d9edf5]">
                    <Camera size={16} />
                    Prendre une photo
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-[#f4fafc] flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#e7f3f8] text-[#246990] flex items-center justify-center">
                <FileText size={18} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Documents</p>
                <p className="text-xs text-gray-500">Les fichiers chargés restent visibles avant l'enregistrement.</p>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Pièce d'identité</p>
                    <p className="text-xs text-gray-500">PDF, JPG ou PNG jusqu'à 5MB</p>
                  </div>
                  <label htmlFor="identity" className="px-3 py-2 rounded-md bg-[#e7f3f8] text-[#246990] border border-[#b8d4e2] text-sm font-medium cursor-pointer">Charger</label>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id="identity" onChange={(e) => handleFileChange(e, 'identity')} />
                </div>
                {identityDoc && renderDocumentList([identityDoc], () => setIdentityDoc(null))}
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Diplômes</p>
                    <p className="text-xs text-gray-500">PDF ou image, 10 Mo par fichier, 5 fichiers au maximum</p>
                  </div>
                  <label htmlFor="diplomes" className="px-3 py-2 rounded-md bg-[#e7f3f8] text-[#246990] border border-[#b8d4e2] text-sm font-medium cursor-pointer">Charger</label>
                  <input type="file" accept=".pdf" className="hidden" id="diplomes" multiple onChange={(e) => handleMultipleFileChange(e, 'diplomas')} />
                </div>
                {diplomas.length > 0 && renderDocumentList(diplomas, (index) => setDiplomas(prev => prev.filter((_, currentIndex) => currentIndex !== index)))}
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Autres documents</p>
                    <p className="text-xs text-gray-500">PDF ou image, 10 Mo par fichier, 10 fichiers au maximum</p>
                  </div>
                  <label htmlFor="autres" className="px-3 py-2 rounded-md bg-[#e7f3f8] text-[#246990] border border-[#b8d4e2] text-sm font-medium cursor-pointer">Charger</label>
                  <input type="file" accept=".pdf" className="hidden" id="autres" multiple onChange={(e) => handleMultipleFileChange(e, 'other')} />
                </div>
                {otherDocs.length > 0 && renderDocumentList(otherDocs, (index) => setOtherDocs(prev => prev.filter((_, currentIndex) => currentIndex !== index)))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedComponent>
  );

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-visible">
          <div className="p-6">
            {/* Indicateur d'étapes */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= step
                        ? 'bg-[#246990] text-white'
                        : 'bg-gray-200 text-gray-600'
                      }`}
                  >
                    {step}
                  </div>
                  {step < 5 && (
                    <div className={`w-16 h-1 ${currentStep > step ? 'bg-[#246990]' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2Famille()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}

              {/* Boutons de navigation */}
              <div className="mt-8 flex justify-end gap-4 border-t pt-6">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(prev => prev - 1)}
                    className="px-6 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    Précédent
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <RefreshCw size={18} />
                    Annuler
                  </button>
                )}

                <button
                  type="submit"
                  className="px-6 py-2.5 text-white bg-[#246990] rounded-lg hover:bg-[#3084b5] flex items-center gap-2"
                >
                  {currentStep === 5 ? (
                    <>
                      <Save size={18} />
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </>
                  ) : (
                    'Suivant'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {toastInfo && (
          shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
            <LongToast
              show
              title={toastInfo.title}
              message={toastInfo.message}
              type={toastInfo.type}
              onClose={() => setToastInfo(null)}
            />
          ) : (
            <Toast
              message={toastInfo.message}
              type={toastInfo.type}
              onClose={() => setToastInfo(null)}
            />
          )
        )}
      </div>

      {/* Modal en dehors du flux principal */}
      {showConfirmation && finance.paiements_mensuels && (
        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => {
            if (!loading) setShowConfirmation(false);
          }}
          onConfirm={handleConfirmSubmission}
          studentInfo={studentInfo}
          parentInfo={parentInfo}
          registrationType={REGISTRATION_TYPE}
          finance={finance}
          classes={classes}
          moisScolaires={moisScolaires}
          familleInfo={familleInfo}
          etatsParMois={etatsParMois}
          isSubmitting={loading}
        />
      )}

    </>
  );
};

export default NewRegistration;
